/**
 * @license
 * SPDX-License-Identifier: Apache-2.5
 */

import React, { useState, useEffect, Suspense } from 'react';
import { 
  ShoppingBag, 
  ShoppingCart,
  User as UserIcon, 
  LogOut, 
  Sparkles, 
  ChevronRight, 
  HelpCircle, 
  ShieldCheck, 
  CreditCard, 
  Clock, 
  ArrowLeft, 
  Truck, 
  Trash2, 
  ShoppingBagIcon, 
  Heart,
  Plus,
  ChevronDown,
  AlertCircle,
  Globe,
  Award,
  Gift,
  Briefcase,
  Percent,
  Key,
  Tag,
  Palette,
  Magnet,
  Phone,
  Smile,
  Menu,
  X,
  Link as LinkIcon,
  Download,
  FileText,
  Home,
  Facebook,
  Instagram,
  CheckCircle2,
  Sliders,
  Layers,
  TrendingDown,
  Edit3,
  Search,
  Lock,
  MapPin,
  GraduationCap,
  ArrowRight,
  Upload,
  Camera,
  Star,
  Zap,
  Check,
  Scissors,
  Info,
  Package,
  Shield,
  Eye,
  RefreshCw,
  Loader2,
  Radio,
  Wifi,
  Share2,
  Video,
  Smartphone,
  ExternalLink
} from 'lucide-react';

import { User, MagnetDesign, Order, AuthState, SavedDesign, PremadeDesign, YourImageItem, CheckoutStep } from './types';
import { 
  trackCustomizerOpen, 
  trackPhotoUploaded, 
  trackTextAdded, 
  trackAddToCart, 
  trackBeginCheckout, 
  trackPurchase, 
  trackPromoCodeApplied, 
  trackMagnetCustomizationComplete,
  trackUetConversion
} from './lib/analytics';
import { useRouteSeo } from './utils/seo';
import { clearDesignerDrafts, removeDesignerDraftById } from './utils/draftStorage';
const MagnetDesigner = React.lazy(() => import('./components/MagnetDesigner'));
const MomentsCreator = React.lazy(() => import('./components/MomentsCreator').then(m => ({ default: m.MomentsCreator })));
const AuthModal = React.lazy(() => import('./components/AuthModal'));
const AdminDashboard = React.lazy(() => import('./components/AdminDashboard'));
const NotificationsTracker = React.lazy(() => import('./components/NotificationsTracker'));
const ChangePasswordModal = React.lazy(() => import('./components/ChangePasswordModal'));
const ProfileModal = React.lazy(() => import('./components/ProfileModal'));
const ElfsightGallery = React.lazy(() => import('./components/ElfsightGallery'));
const ElfsightFAQ = React.lazy(() => import('./components/ElfsightFAQ'));
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./components/TermsOfService'));
const PremadeBrowser = React.lazy(() => import('./components/PremadeBrowser'));
const NewsPage = React.lazy(() => import('./components/NewsPage'));
const FundraisingPage = React.lazy(() => import('./components/FundraisingPage'));
const GiftCardsPage = React.lazy(() => import('./components/GiftCardsPage'));
const UseCaseLandingPage = React.lazy(() => import('./components/UseCaseLandingPage'));
const CheckoutWizard = React.lazy(() => import('./components/CheckoutWizard'));
import { isUseCaseSlug, UseCaseSlug } from './data/useCasePages';
import { getOrderItemPricing } from './utils/invoiceGenerator';
const generateInvoicePDF = (...args: Parameters<typeof import('./utils/invoiceGenerator')['generateInvoicePDF']>) => 
  import('./utils/invoiceGenerator').then(m => m.generateInvoicePDF(...args));

// High-performance intelligent prefetching functions to instantly load route modules
const preloadFundraising = () => import('./components/FundraisingPage');
const preloadGiftCards = () => import('./components/GiftCardsPage');
const preloadUseCasePage = () => import('./components/UseCaseLandingPage');
const preloadMagnetDesigner = () => import('./components/MagnetDesigner');
const preloadMomentsCreator = () => import('./components/MomentsCreator');
const preloadPremade = () => import('./components/PremadeBrowser');
const preloadNews = () => import('./components/NewsPage');
const preloadCheckoutWizard = () => import('./components/CheckoutWizard');
const preloadAdmin = () => import('./components/AdminDashboard');
const preloadAuth = () => import('./components/AuthModal');
const preloadElfsight = () => Promise.all([import('./components/ElfsightGallery'), import('./components/ElfsightFAQ')]);

// Toggle to restrict shipping to Canadian addresses only (or easily disable to allow US/international shipping in the future)
const ALLOW_ONLY_CANADA_SHIPPING = true;

// Helper utility to safely serialize lightweight objects for localStorage
function safeSanitizeForStorage<T>(obj: T): T {
  if (!obj) return obj;
  if (typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    return obj.map(item => safeSanitizeForStorage(item)) as unknown as T;
  }

  const sanitized: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      sanitized[key] = obj[key];
    }
  }
  return sanitized as T;
}

export default function App() {
  // Dynamic pricing state managed by admin
  const [pricing, setPricing] = useState<{
    pricePerMagnet: number;
    shippingRate: number;
    freeShippingThreshold: number;
    scaledDiscounts: Array<{ minQuantity: number; pricePerMagnet: number }>;
    enableKamloopsDiscount?: boolean;
    kamloopsShippingRate?: number;
    localDeliveryCityName?: string;
    localDeliveryPostalPrefixes?: string;
    enableFreeShipping?: boolean;
  }>(() => {
    try {
      const cached = localStorage.getItem('square_magnets_pricing');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === 'object') {
          return {
            pricePerMagnet: typeof parsed.pricePerMagnet === 'number' ? parsed.pricePerMagnet : 5.99,
            shippingRate: typeof parsed.shippingRate === 'number' ? parsed.shippingRate : 10.00,
            freeShippingThreshold: typeof parsed.freeShippingThreshold === 'number' ? parsed.freeShippingThreshold : 75.00,
            scaledDiscounts: Array.isArray(parsed.scaledDiscounts) && parsed.scaledDiscounts.length > 0
              ? parsed.scaledDiscounts
              : [
                  { minQuantity: 2, pricePerMagnet: 5.59 },
                  { minQuantity: 6, pricePerMagnet: 5.49 },
                  { minQuantity: 9, pricePerMagnet: 5.25 },
                  { minQuantity: 12, pricePerMagnet: 4.99 },
                  { minQuantity: 18, pricePerMagnet: 4.49 },
                  { minQuantity: 24, pricePerMagnet: 3.99 },
                  { minQuantity: 48, pricePerMagnet: 3.00 },
                  { minQuantity: 250, pricePerMagnet: 2.49 }
                ],
            enableKamloopsDiscount: parsed.enableKamloopsDiscount !== undefined ? !!parsed.enableKamloopsDiscount : true,
            kamloopsShippingRate: typeof parsed.kamloopsShippingRate === 'number' ? parsed.kamloopsShippingRate : 5.00,
            localDeliveryCityName: parsed.localDeliveryCityName || 'Kamloops',
            localDeliveryPostalPrefixes: parsed.localDeliveryPostalPrefixes || 'V2C, V2B, V2E, V1S, V2H',
            enableFreeShipping: parsed.enableFreeShipping !== undefined ? !!parsed.enableFreeShipping : false
          };
        }
      }
    } catch (e) {
      // Graceful fallback
    }
    return {
      pricePerMagnet: 5.99,
      shippingRate: 10.00,
      freeShippingThreshold: 75.00,
      scaledDiscounts: [
        { minQuantity: 2, pricePerMagnet: 5.59 },
        { minQuantity: 6, pricePerMagnet: 5.49 },
        { minQuantity: 9, pricePerMagnet: 5.25 },
        { minQuantity: 12, pricePerMagnet: 4.99 },
        { minQuantity: 18, pricePerMagnet: 4.49 },
        { minQuantity: 24, pricePerMagnet: 3.99 },
        { minQuantity: 48, pricePerMagnet: 3.00 },
        { minQuantity: 250, pricePerMagnet: 2.49 }
      ],
      enableKamloopsDiscount: true,
      kamloopsShippingRate: 5.00,
      localDeliveryCityName: 'Kamloops',
      localDeliveryPostalPrefixes: 'V2C, V2B, V2E, V1S, V2H',
      enableFreeShipping: false
    };
  });

  const [pricingLoaded, setPricingLoaded] = useState(() => {
    try {
      return localStorage.getItem('square_magnets_pricing') !== null;
    } catch (e) {
      return false;
    }
  });

  const getUnitPriceForQuantity = (qty: number) => {
    let price = pricing.pricePerMagnet;
    const sorted = [...pricing.scaledDiscounts].sort((a, b) => b.minQuantity - a.minQuantity);
    for (const slab of sorted) {
      if (qty >= slab.minQuantity) {
        price = slab.pricePerMagnet;
        break;
      }
    }
    return price;
  };

  const fetchPricing = async () => {
    try {
      const response = await fetch('/api/pricing');
      if (response.ok) {
        const data = await response.json();
        const resolvedPricing = {
          pricePerMagnet: data.pricePerMagnet,
          shippingRate: data.shippingRate,
          freeShippingThreshold: data.freeShippingThreshold,
          scaledDiscounts: (data.scaledDiscounts && data.scaledDiscounts.length > 0) 
            ? data.scaledDiscounts 
            : [
                { minQuantity: 2, pricePerMagnet: 5.59 },
                { minQuantity: 6, pricePerMagnet: 5.49 },
                { minQuantity: 9, pricePerMagnet: 5.25 },
                { minQuantity: 12, pricePerMagnet: 4.99 },
                { minQuantity: 18, pricePerMagnet: 4.49 },
                { minQuantity: 24, pricePerMagnet: 3.99 },
                { minQuantity: 48, pricePerMagnet: 3.00 },
                { minQuantity: 250, pricePerMagnet: 2.49 }
              ],
          enableKamloopsDiscount: data.enableKamloopsDiscount !== undefined ? data.enableKamloopsDiscount : true,
          kamloopsShippingRate: data.kamloopsShippingRate !== undefined ? data.kamloopsShippingRate : 5.00,
          localDeliveryCityName: data.localDeliveryCityName || 'Kamloops',
          localDeliveryPostalPrefixes: data.localDeliveryPostalPrefixes || 'V2C, V2B, V2E, V1S, V2H',
          enableFreeShipping: data.enableFreeShipping !== undefined ? data.enableFreeShipping : false
        };
        setPricing(resolvedPricing);
        try {
          localStorage.setItem('square_magnets_pricing', JSON.stringify(resolvedPricing));
        } catch (e) {
          // ignore localStorage failure on restricted/private browsing modes
        }
      }
    } catch (err) {
      console.error('Failed fetching dynamic operational pricing config:', err);
    } finally {
      setPricingLoaded(true);
    }
  };

  // Authentication states
  const [auth, setAuth] = useState<AuthState>({
    token: localStorage.getItem('magnet_token'),
    user: localStorage.getItem('magnet_user') ? JSON.parse(localStorage.getItem('magnet_user')!) : null,
  });

  const [stepConfigs, setStepConfigs] = useState<any[]>([]);

  const fetchStepConfigs = async () => {
    try {
      const response = await fetch('/api/step-notifications');
      if (response.ok) {
        const data = await response.json();
        setStepConfigs(data || []);
      }
    } catch (err) {
      console.error('Failed fetching step notifications config:', err);
    }
  };

  const mapStatusToLabel = (status: string) => {
    const found = stepConfigs.find(c => c.id === status);
    if (found) return found.title;
    if (status === 'completed') return 'In Production';
    if (status === 'paid') return 'Paid Preparing';
    if (status === 'shipped') return 'Dispatched Transit';
    if (status === 'delivered') return 'Delivered';
    if (status === 'pending') return 'Unpaid Checkout';
    if (status === 'refunded') return 'Refunded';
    if (status === 'cancelled' || status === 'failed') return 'Failed Payment';
    return status;
  };

  useEffect(() => {
    fetchPricing();

    // Idle-time prefetching of high-traffic route chunks - deferred to prevent mobile contention during initial render
    const prefetchIdleRoutes = () => {
      // Do not prefetch if user has Save-Data enabled or on mobile/metered connections (2g/3g/4g)
      if (typeof navigator !== 'undefined' && 'connection' in navigator) {
        const conn = (navigator as any).connection;
        if (conn && (conn.saveData || conn.effectiveType === '2g' || conn.effectiveType === '3g' || conn.effectiveType === '4g')) {
          return;
        }
      }
      preloadFundraising();
      preloadMagnetDesigner();
      preloadPremade();
      preloadMomentsCreator();
      preloadNews();
      preloadCheckoutWizard();
    };

    if (typeof window !== 'undefined') {
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(prefetchIdleRoutes, { timeout: 16000 });
      } else {
        setTimeout(prefetchIdleRoutes, 14000);
      }
    }
  }, [auth.token]);

  const [showAuthModal, setShowAuthModal] = useState(() => {
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search);
      return p.get('mode') === 'resetPassword' || Boolean(p.get('oobCode'));
    }
    return false;
  });
  const [authModalMessage, setAuthModalMessage] = useState<string | undefined>(undefined);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const parseInitialUrlParams = (): { active: 'home' | 'shop' | 'moments' | 'cart' | 'dashboard' | 'admin' | 'privacy' | 'terms' | 'news' | 'browse' | 'fundraising' | 'giftcards' | UseCaseSlug; dashboard: 'orders' | 'saved_designs'; newsSlug: string | null; premadeId: string | null } => {
    if (typeof window === 'undefined') {
      return { active: 'home', dashboard: 'orders', newsSlug: null, premadeId: null };
    }
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash.toLowerCase().replace('#', '');
    const pageVal = params.get('page') || params.get('tab') || hash;
    const paymentStatus = params.get('payment');
    const premadeId = params.get('premade') || params.get('premade_id') || params.get('premadeDesign');

    if (premadeId) {
      return { active: 'browse', dashboard: 'orders', newsSlug: null, premadeId };
    }

    // Parse direct paths: /news, /fundraising, /studio, /moments, /cart, /track, /browse, /privacy, /terms, and 5 use-case landing pages
    const path = window.location.pathname.toLowerCase().replace(/\/$/, '');
    const cleanPathSlug = path.replace(/^\//, '');
    if (isUseCaseSlug(cleanPathSlug)) {
      return { active: cleanPathSlug, dashboard: 'orders', newsSlug: null, premadeId: null };
    }

    if (path.startsWith('/news')) {
      const parts = path.split('/');
      const slug = parts[2] ? parts[2] : null;
      return { active: 'news', dashboard: 'orders', newsSlug: slug, premadeId: null };
    }

    if (path.startsWith('/fundraising') || path.startsWith('/fundraise')) {
      return { active: 'fundraising', dashboard: 'orders', newsSlug: null, premadeId: null };
    }

    if (path === '/gift-cards' || path === '/giftcards' || path === '/gift-card') {
      return { active: 'giftcards', dashboard: 'orders', newsSlug: null, premadeId: null };
    }

    if (path === '/studio' || path === '/customize' || path === '/shop') {
      return { active: 'shop', dashboard: 'orders', newsSlug: null, premadeId: null };
    }

    if (path === '/moments') {
      return { active: 'moments', dashboard: 'orders', newsSlug: null, premadeId: null };
    }

    if (path === '/cart' || path === '/checkout') {
      return { active: 'cart', dashboard: 'orders', newsSlug: null, premadeId: null };
    }

    if (path === '/track' || path === '/orders') {
      return { active: 'dashboard', dashboard: 'orders', newsSlug: null, premadeId: null };
    }

    if (path === '/browse') {
      return { active: 'browse', dashboard: 'orders', newsSlug: null, premadeId: null };
    }

    if (path === '/privacy') {
      return { active: 'privacy', dashboard: 'orders', newsSlug: null, premadeId: null };
    }

    if (path === '/terms') {
      return { active: 'terms', dashboard: 'orders', newsSlug: null, premadeId: null };
    }

    if (paymentStatus === 'giftcard_success' || paymentStatus === 'giftcard_cancelled') {
      return { active: 'giftcards', dashboard: 'orders', newsSlug: null, premadeId: null };
    }
    if (paymentStatus === 'success') {
      return { active: 'dashboard', dashboard: 'orders', newsSlug: null, premadeId: null };
    }
    if (paymentStatus === 'cancelled') {
      return { active: 'cart', dashboard: 'orders', newsSlug: null, premadeId: null };
    }

    if (pageVal) {
      const rawHyphenated = pageVal.toLowerCase().trim();
      if (isUseCaseSlug(rawHyphenated)) {
        return { active: rawHyphenated, dashboard: 'orders', newsSlug: null, premadeId: null };
      }
      const cleanVal = pageVal.toLowerCase().replace(/[-_]/g, '');
      if (cleanVal.startsWith('fundraising') || cleanVal.startsWith('fundraise') || cleanVal.startsWith('campaign')) {
        return { active: 'fundraising', dashboard: 'orders', newsSlug: null, premadeId: null };
      }
      if (cleanVal === 'giftcards' || cleanVal === 'giftcard' || cleanVal === 'gifts') {
        return { active: 'giftcards', dashboard: 'orders', newsSlug: null, premadeId: null };
      }
      if (cleanVal === 'news' || cleanVal === 'blog') {
        const slugVal = params.get('post') || params.get('slug') || null;
        return { active: 'news', dashboard: 'orders', newsSlug: slugVal, premadeId: null };
      }
      if (cleanVal === 'browse' || cleanVal === 'premade' || cleanVal === 'premadedesigns' || cleanVal === 'gallery') {
        const pId = params.get('id') || params.get('premade');
        return { active: 'browse', dashboard: 'orders', newsSlug: null, premadeId: pId };
      }
      if (cleanVal === 'shop' || cleanVal === 'customize' || cleanVal === 'upload') {
        return { active: 'shop', dashboard: 'orders', newsSlug: null, premadeId: null };
      }
      if (cleanVal === 'moments' || cleanVal === 'momentscreator' || cleanVal === 'moments_creator') {
        return { active: 'moments', dashboard: 'orders', newsSlug: null, premadeId: null };
      }
      if (cleanVal === 'cart' || cleanVal === 'checkout') {
        return { active: 'cart', dashboard: 'orders', newsSlug: null, premadeId: null };
      }
      if (cleanVal === 'privacy') {
        return { active: 'privacy', dashboard: 'orders', newsSlug: null, premadeId: null };
      }
      if (cleanVal === 'terms') {
        return { active: 'terms', dashboard: 'orders', newsSlug: null, premadeId: null };
      }
      if (cleanVal === 'admin') {
        return { active: 'admin', dashboard: 'orders', newsSlug: null, premadeId: null };
      }
      if (cleanVal === 'saveddesigns' || cleanVal === 'mydesigns' || cleanVal === 'designs' || cleanVal === 'saved_designs' || cleanVal === 'my_designs') {
        return { active: 'dashboard', dashboard: 'saved_designs', newsSlug: null, premadeId: null };
      }
      if (cleanVal === 'orders' || cleanVal === 'dashboard') {
        return { active: 'dashboard', dashboard: 'orders', newsSlug: null, premadeId: null };
      }
    }
    return { active: 'home', dashboard: 'orders', newsSlug: null, premadeId: null };
  };

  const [activeTab, setActiveTab] = useState<'home' | 'shop' | 'moments' | 'cart' | 'dashboard' | 'admin' | 'privacy' | 'terms' | 'browse' | 'news' | 'fundraising' | 'giftcards' | UseCaseSlug>(() => {
    return parseInitialUrlParams().active;
  });

  // Sticky "Design Magnets" bottom bar for mobile when browsing past the hero section
  const [showStickyMobileBar, setShowStickyMobileBar] = useState(false);

  useEffect(() => {
    if (activeTab !== 'home') {
      setShowStickyMobileBar(false);
      return;
    }

    const checkHeroScroll = () => {
      // Past hero section (~350px)
      setShowStickyMobileBar(window.scrollY > 350);
    };

    window.addEventListener('scroll', checkHeroScroll, { passive: true });
    checkHeroScroll();
    return () => window.removeEventListener('scroll', checkHeroScroll);
  }, [activeTab]);
  const [selectedNewsSlug, setSelectedNewsSlug] = useState<string | null>(() => {
    return parseInitialUrlParams().newsSlug;
  });
  const [initialPremadeId, setInitialPremadeId] = useState<string | null>(() => {
    return parseInitialUrlParams().premadeId;
  });

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [calcQuantity, setCalcQuantity] = useState<number>(12);
  const [activeGalleryTab, setActiveGalleryTab] = useState<'photos' | 'business' | 'moments' | 'personality'>('photos');
  const [openHomeFaq, setOpenHomeFaq] = useState<number | null>(null);
  const [editingDesignId, setEditingDesignId] = useState<string | null>(null);
  const [customDesignToLoad, setCustomDesignToLoad] = useState<YourImageItem | null>(null);

  // Customer Shopping active cart with local storage backup to survive Stripe payment redirects
  const [cart, setCart] = useState<MagnetDesign[]>(() => {
    try {
      const cached = localStorage.getItem('square_magnets_cart');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          return parsed.map(item => {
            // Restore any corrupted SVG string if croppedImageUrl exists
            if ((!item.imageUrl || item.imageUrl.startsWith('data:image/svg+xml')) && item.croppedImageUrl && !item.croppedImageUrl.startsWith('data:image/svg+xml')) {
              return { ...item, imageUrl: item.croppedImageUrl };
            }
            return item;
          });
        }
      }
    } catch (e) {
      console.warn('Failed to load cart from localStorage', e);
    }
    return [];
  });

  // Automatically sync cart state to localStorage safely within quota limits
  useEffect(() => {
    try {
      const lightCart = cart.map(item => ({
        ...item,
        imageUrl: (item.imageUrl && item.imageUrl.length > 250000 && item.croppedImageUrl) ? item.croppedImageUrl : item.imageUrl,
        originalImageUrl: undefined,
        overlayImageUrl: (item.overlayImageUrl && item.overlayImageUrl.length > 50000) ? undefined : item.overlayImageUrl
      }));
      localStorage.setItem('square_magnets_cart', JSON.stringify(lightCart));
    } catch (e) {
      try {
        const compactCart = cart.map(item => ({
          ...item,
          imageUrl: item.croppedImageUrl || '',
          originalImageUrl: undefined,
          overlayImageUrl: undefined
        }));
        localStorage.setItem('square_magnets_cart', JSON.stringify(compactCart));
      } catch (backupErr) {
        // Silently tolerate storage quota constraints without noisy console warnings
      }
    }
  }, [cart]);

  // Centralized safety check: clear editingDesignId if the current tab does not match the active design type (Moments vs standard)
  useEffect(() => {
    if (editingDesignId) {
      const design = cart.find(c => c.id === editingDesignId);
      if (design) {
        if (design.isMomentsTemplate && activeTab === 'shop') {
          setEditingDesignId(null);
        } else if (!design.isMomentsTemplate && activeTab === 'moments') {
          setEditingDesignId(null);
        }
      }
    }
  }, [activeTab, editingDesignId, cart]);

  // Selected design item for full-screen Interactive Print Proof zooming
  const [previewItem, setPreviewItem] = useState<MagnetDesign | null>(null);
  const [proofMode, setProofMode] = useState<'finished' | 'bleed'>('finished');

  // Coupon/Promo Code client states
  const [couponCodeInput, setCouponCodeInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<any | null>(() => {
    try {
      const cached = localStorage.getItem('square_magnets_applied_coupon');
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {
      console.warn('Failed to load applied coupon from localStorage', e);
    }
    return null;
  });
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  // Automatically sync applied coupon state to localStorage whenever it changes
  useEffect(() => {
    try {
      if (appliedCoupon) {
        localStorage.setItem('square_magnets_applied_coupon', JSON.stringify(appliedCoupon));
      } else {
        localStorage.removeItem('square_magnets_applied_coupon');
      }
    } catch (e) {
      console.warn('Failed to save applied coupon to localStorage', e);
    }
  }, [appliedCoupon]);

  // Selected order details for deep review/tracking (Customer view)
  const [selectedOrderTracking, setSelectedOrderTracking] = useState<Order | null>(null);

  // Load customizer typography on demand when user opens Studio or Moments
  useEffect(() => {
    if (activeTab === 'shop' || activeTab === 'moments') {
      if (typeof window !== 'undefined' && (window as any).loadCustomizerFonts) {
        (window as any).loadCustomizerFonts();
      }
    }
  }, [activeTab]);

  // Fetch step configs lazily when dashboard or tracking is viewed
  useEffect(() => {
    if (activeTab === 'dashboard' || activeTab === 'admin' || selectedOrderTracking) {
      if (stepConfigs.length === 0) {
        fetchStepConfigs();
      }
    }
    if (activeTab === 'dashboard') {
      if (auth.token) {
        fetchOrderHistory();
      }
      if (guestLookedUpOrder?.id && guestLookedUpOrder?.customerEmail) {
        fetch('/api/orders/lookup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderId: guestLookedUpOrder.id,
            email: guestLookedUpOrder.customerEmail
          })
        })
          .then(r => (r.ok ? r.json() : null))
          .then(freshOrder => {
            if (freshOrder && freshOrder.id) {
              setGuestLookedUpOrder(freshOrder);
              if (selectedOrderTracking?.id === freshOrder.id) {
                setSelectedOrderTracking(freshOrder);
              }
            }
          })
          .catch(() => {});
      }
    }
  }, [activeTab]);

  // States for orders history
  const [orderHistory, setOrderHistory] = useState<Order[]>(() => {
    try {
      const userStr = localStorage.getItem('magnet_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const cached = localStorage.getItem(`magnet_orders_${user.id}`);
        if (cached) return JSON.parse(cached);
      }
    } catch (e) {
      console.warn('Failed to initialize orderHistory from cache', e);
    }
    return [];
  });
  const [loadingOrders, setLoadingOrders] = useState(false);

  // States for user saved designs
  const [savedDesigns, setSavedDesigns] = useState<SavedDesign[]>(() => {
    try {
      const userStr = localStorage.getItem('magnet_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const cached = localStorage.getItem(`magnet_saved_designs_${user.id}`);
        if (cached) return JSON.parse(cached);
      }
    } catch (e) {
      console.warn('Failed to initialize savedDesigns from cache', e);
    }
    return [];
  });
  const [loadingSavedDesigns, setLoadingSavedDesigns] = useState(false);

  // Automatically sync saved designs to localStorage safely within quota limits
  useEffect(() => {
    if (auth.user?.id) {
      try {
        const lightDesigns = (savedDesigns || []).slice(0, 10).map(sd => ({
          ...sd,
          imageUrl: (sd.imageUrl && sd.imageUrl.length > 250000 && sd.croppedImageUrl) ? sd.croppedImageUrl : sd.imageUrl,
          originalImageUrl: undefined,
          overlayImageUrl: undefined
        }));
        localStorage.setItem(`magnet_saved_designs_${auth.user.id}`, JSON.stringify(lightDesigns));
      } catch (e) {
        // Silently tolerate browser quota constraints without noisy console warnings
      }
    }
  }, [savedDesigns, auth.user?.id]);

  // Automatically sync order history to localStorage safely within quota limits
  useEffect(() => {
    if (auth.user?.id) {
      try {
        const lightOrders = (orderHistory || []).slice(0, 5).map(ord => ({
          ...ord,
          magnets: (ord.magnets || []).map(m => ({
            ...m,
            imageUrl: m.croppedImageUrl || '',
            originalImageUrl: undefined,
            overlayImageUrl: undefined
          }))
        }));
        localStorage.setItem(`magnet_orders_${auth.user.id}`, JSON.stringify(lightOrders));
      } catch (e) {
        // Silently tolerate browser quota constraints without noisy console warnings
      }
    }
  }, [orderHistory, auth.user?.id]);
  const [designToSave, setDesignToSave] = useState<Omit<MagnetDesign, 'id'> | null>(null);
  const [editingSavedDesignId, setEditingSavedDesignId] = useState<string | null>(null);
  const [saveAsNewCopy, setSaveAsNewCopy] = useState(false);
  const [saveDesignName, setSaveDesignName] = useState('');
  const [showSaveDesignModal, setShowSaveDesignModal] = useState(false);
  const [savingDesignState, setSavingDesignState] = useState(false);
  const [designToDelete, setDesignToDelete] = useState<SavedDesign | null>(null);
  const [designToRename, setDesignToRename] = useState<SavedDesign | null>(null);
  const [renameInputName, setRenameInputName] = useState('');
  const [deletingInProgress, setDeletingInProgress] = useState(false);
  const [renamingInProgress, setRenamingInProgress] = useState(false);
  
  // UX simplification states
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [editingSavedDesignIdForInline, setEditingSavedDesignIdForInline] = useState<string | null>(null);

  // Auto-reset applied coupon if cart is emptied (unless it's a fundraiser campaign)
  useEffect(() => {
    if (cart.length === 0 && appliedCoupon && !appliedCoupon.isFundraiser) {
      setAppliedCoupon(null);
      setCouponCodeInput('');
      setCouponError('');
    }
  }, [cart, appliedCoupon]);

  const getSavedDesignForMagnet = (design: MagnetDesign) => {
    if (!savedDesigns || !design) return null;
    return savedDesigns.find(sd => 
      sd.design.croppedImageUrl === design.croppedImageUrl || 
      (sd.design.imageUrl === design.imageUrl && sd.design.text?.text === design.text?.text && sd.design.filter === design.filter)
    ) || null;
  };

  const handleApplyCoupon = async (codeStr: string) => {
    if (!codeStr.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const res = await fetch('/api/coupons/apply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: codeStr.trim(),
          subtotal: cartSubtotal,
          totalQuantity: cartItemCount,
          unitPrice: unitPrice,
          shippingCost: shippingCost
        })
      });
      const resText = await res.text();
      let data: any = null;
      try {
        data = resText ? JSON.parse(resText) : null;
      } catch (parseErr) {}

      if (!res.ok) {
        throw new Error(data?.error || (res.status === 503 ? 'Service temporarily busy. Please try again in a moment.' : 'Failed to apply coupon'));
      }
      setAppliedCoupon(data);
      setCouponError('');
      if (data.isGiftCard) {
        const avail = Number(data.remainingBalance ?? data.discountValue ?? 0);
        setToast({
          message: `Digital Gift Card "${data.code}" applied ($${avail.toFixed(2)} CAD available)!`,
          type: 'success'
        });
      } else if (data.isFundraiser) {
        if (data.fundraiserAllowFreeSchoolDelivery) {
          setFundraiserDeliveryMethod('school');
        } else {
          setFundraiserDeliveryMethod('home');
        }
        setToast({
          message: `Campaign Active: ${data.fundraiserName || data.code}!`,
          type: 'success'
        });
      } else {
        setToast({
          message: `Coupon "${data.code}" successfully applied!`,
          type: 'success'
        });
      }
    } catch (err: any) {
      setCouponError(err.message || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCodeInput('');
    setCouponError('');
  };

  // Auto-apply fundraiser or coupon code on load if present in query parameters
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const fundraiserCode = params.get('fundraiser') || params.get('coupon');
      if (fundraiserCode) {
        const cleanCode = fundraiserCode.trim().toUpperCase();
        // Avoid infinite loops by checking if it's already applied
        if (!appliedCoupon || appliedCoupon.code !== cleanCode) {
          handleApplyCoupon(cleanCode);
          // Clean up URL parameters to keep the address bar clean
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
        }
      }
    }
  }, [appliedCoupon]);

  const getCouponUsageExplanation = (cp: any) => {
    if (!cp) return '';
    if (cp.isGiftCard) {
      const avail = Number(cp.remainingBalance ?? cp.discountValue ?? 0);
      return `Digital Gift Card Balance: $${avail.toFixed(2)} CAD available (applies toward magnets & shipping)`;
    }
    let text = '';
    switch (cp.discountType) {
      case 'percentage':
        text = `${cp.discountValue}% off your entire order`;
        break;
      case 'percentage_cap':
        text = `${cp.discountValue}% off your order`;
        if (cp.maxDiscount) {
          text += ` up to a maximum of $${cp.maxDiscount.toFixed(2)}`;
        }
        break;
      case 'fixed_amount':
        text = `Flat $${cp.discountValue.toFixed(2)} off your order`;
        break;
      case 'buy_x_get_y':
        text = `Buy ${cp.buyQty || 1} Get ${cp.freeQty || 1} Free promotion`;
        break;
      case 'fixed_item_price':
        text = `Special price of $${cp.discountValue.toFixed(2)} per magnet`;
        if (cp.maxMagnets) {
          text += ` (valid for up to ${cp.maxMagnets} magnets)`;
        }
        break;
      case 'none':
        text = cp.isFundraiser 
          ? `Attributed to ${cp.fundraiserName || 'this fundraiser campaign'}`
          : 'Coupon applied (no order discount)';
        break;
      default:
        text = `Discount applied`;
    }

    if (cp.hasShippingOverride) {
      const shPrice = cp.shippingPriceOverride ?? 0;
      if (shPrice === 0) {
        text += ' and Free Shipping';
      } else {
        text += ` and $${shPrice.toFixed(2)} Shipping`;
      }
    }
    
    const rules: string[] = [text];
    if (cp.minMagnets) {
      rules.push(`requires a minimum of ${cp.minMagnets} magnets to qualify`);
    }
    
    if (cp.maxDiscount && cp.discountType !== 'percentage_cap') {
      rules.push(`capped at a maximum savings of $${cp.maxDiscount.toFixed(2)}`);
    }

    if (rules.length > 1) {
      const [base, ...extra] = rules;
      return `${base} — ${extra.join(', ')}`;
    }
    return text;
  };
  const [inlineRenameValue, setInlineRenameValue] = useState('');
  const [inlineRenamingInProgress, setInlineRenamingInProgress] = useState<string | null>(null);

  const [dashboardTab, setDashboardTab] = useState<'orders' | 'saved_designs'>(() => {
    return parseInitialUrlParams().dashboard;
  });

  // Dynamic route metadata & JSON-LD structured data hydration
  useRouteSeo({
    activeTab,
    dashboardTab,
    selectedNewsSlug,
    pricing
  });

  // Stripe callback states
  const [paymentSuccessOrder, setPaymentSuccessOrder] = useState<Order | null>(null);
  const [paymentCancelled, setPaymentCancelled] = useState(false);
  const [isFetchingOrderAfterCheckout, setIsFetchingOrderAfterCheckout] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('payment') === 'success' && !!params.get('orderId');
    }
    return false;
  });

  // Address checkout form
  const [shippingName, setShippingName] = useState(auth.user?.name || '');
  const [guestEmail, setGuestEmail] = useState(auth.user?.email || '');
  const [customerPhone, setCustomerPhone] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingZip, setShippingZip] = useState('');
  const [shippingCountry, setShippingCountry] = useState('Canada');

  // Billing address checkout states (separated if different from shipping)
  const [billingSameAsShipping, setBillingSameAsShipping] = useState<boolean>(true);
  const [billingName, setBillingName] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [billingCountry, setBillingCountry] = useState('Canada');

  const [isGift, setIsGift] = useState(false);
  const [giftMessage, setGiftMessage] = useState('');
  const [giftTo, setGiftTo] = useState('');
  const [giftFrom, setGiftFrom] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [allowSocialMediaFeatured, setAllowSocialMediaFeatured] = useState<boolean>(true);
  const [fundraiserStudentName, setFundraiserStudentName] = useState('');
  const [fundraiserClassroom, setFundraiserClassroom] = useState('');
  const [fundraiserDeliveryMethod, setFundraiserDeliveryMethod] = useState<'school' | 'home'>('home');

  // Autocomplete suggestions states (OpenStreetMap) for Shipping Address
  const [addressSuggestions, setAddressSuggestions] = useState<{ displayName: string; street: string; city: string; postalCode: string }[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  const [lastSelectedStreet, setLastSelectedStreet] = useState('');

  // Autocomplete suggestions states for Billing Address
  const [billingAddressSuggestions, setBillingAddressSuggestions] = useState<{ displayName: string; street: string; city: string; postalCode: string }[]>([]);
  const [showBillingSuggestions, setShowBillingSuggestions] = useState(false);
  const [isFetchingBillingSuggestions, setIsFetchingBillingSuggestions] = useState(false);
  const [lastSelectedBillingStreet, setLastSelectedBillingStreet] = useState('');

  // Multi-step Checkout Step State ('cart' | 'shipping' | 'billing' | 'review')
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>('cart');

  useEffect(() => {
    if (!shippingAddress.trim() || shippingAddress.trim().length < 4 || shippingAddress === lastSelectedStreet) {
      setAddressSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsFetchingSuggestions(true);
      try {
        const countryParam = ALLOW_ONLY_CANADA_SHIPPING ? 'ca' : (shippingCountry === 'Canada' ? 'ca' : 'us,ca');
        const res = await fetch(`/api/autocomplete-address?q=${encodeURIComponent(shippingAddress)}&countryCode=${countryParam}`);
        if (res.ok) {
          const data = await res.json();
          setAddressSuggestions(data);
        }
      } catch (err) {
        console.error('Error fetching address suggestions:', err);
      } finally {
        setIsFetchingSuggestions(false);
      }
    }, 450);

    return () => clearTimeout(timer);
  }, [shippingAddress, lastSelectedStreet]);

  // Billing address autocomplete query
  useEffect(() => {
    if (billingSameAsShipping || !billingAddress.trim() || billingAddress.trim().length < 4 || billingAddress === lastSelectedBillingStreet) {
      setBillingAddressSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsFetchingBillingSuggestions(true);
      try {
        const countryParam = ALLOW_ONLY_CANADA_SHIPPING ? 'ca' : (billingCountry === 'Canada' ? 'ca' : 'us,ca');
        const res = await fetch(`/api/autocomplete-address?q=${encodeURIComponent(billingAddress)}&countryCode=${countryParam}`);
        if (res.ok) {
          const data = await res.json();
          setBillingAddressSuggestions(data);
        }
      } catch (err) {
        console.error('Error fetching billing address suggestions:', err);
      } finally {
        setIsFetchingBillingSuggestions(false);
      }
    }, 450);

    return () => clearTimeout(timer);
  }, [billingAddress, lastSelectedBillingStreet, billingSameAsShipping, billingCountry]);

  // Guest order lookup state
  const [lookupOrderId, setLookupOrderId] = useState('');
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupError, setLookupError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [guestLookedUpOrder, setGuestLookedUpOrder] = useState<Order | null>(null);

  // Sync details when user auth changes
  useEffect(() => {
    if (auth.user) {
      setShippingName(auth.user.name || '');
      setGuestEmail(auth.user.email || '');
    } else {
      setShippingName('');
      setGuestEmail('');
    }
  }, [auth.user]);

  // Update Microsoft Advertising UET Enhanced Conversions when user email changes
  useEffect(() => {
    const email = guestEmail.trim() || auth.user?.email || '';
    if (email) {
      trackUetConversion(email);
    }
  }, [guestEmail, auth.user?.email]);

  // OpenStreetMap matching states for Kamloops discounted shipping
  const [isLocalKamloopsAddress, setIsLocalKamloopsAddress] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeStatus, setGeocodeStatus] = useState<'idle' | 'success' | 'non_local' | 'error'>('idle');

  // Custom Toast helper
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type });
  };

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Query server to geocode address against OpenStreetMap's Nominatim helper
  const checkLocalShippingDiscount = async (addressStr: string, cityStr: string, zipStr: string) => {
    const cleanAddress = (addressStr || '').trim();
    const cleanCity = (cityStr || '').trim();
    const cleanZip = (zipStr || '').trim().replace(/\s+/g, '').toUpperCase();

    if (!cleanCity && !cleanAddress && !cleanZip) {
      setIsLocalKamloopsAddress(false);
      setGeocodeStatus('idle');
      return;
    }

    // Actual street delivery address is strictly REQUIRED to qualify for local rate!
    if (!cleanAddress || cleanAddress.length < 3 || cleanAddress.toLowerCase().includes('express checkout')) {
      setIsLocalKamloopsAddress(false);
      setGeocodeStatus((cleanCity || cleanZip) ? 'missing_address' : 'idle');
      return;
    }

    const targetCity = (pricing.localDeliveryCityName || 'Kamloops').trim();
    const targetCityLower = targetCity.toLowerCase();
    const rawPrefixes = pricing.localDeliveryPostalPrefixes || 'V2C, V2B, V2E, V1S, V2H';
    const allowedPrefixes = rawPrefixes.split(',').map((p: string) => p.trim().toUpperCase()).filter(Boolean);

    const isCityMatch = cleanCity.toLowerCase().includes(targetCityLower) || cleanAddress.toLowerCase().includes(targetCityLower);
    const isPostalMatch = allowedPrefixes.some((pfx: string) => cleanZip.startsWith(pfx));

    setIsGeocoding(true);
    try {
      const response = await fetch('/api/geocode-kamloops', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          address: cleanAddress,
          city: cleanCity || (isCityMatch ? targetCity : ''),
          zip: cleanZip,
        })
      });

      if (response.ok) {
        const data = await response.json();
        const verified = Boolean(data.isKamloops);
        setIsLocalKamloopsAddress(verified);
        setGeocodeStatus(verified ? 'success' : 'non_local');
      } else {
        setIsLocalKamloopsAddress(false);
        setGeocodeStatus('error');
      }
    } catch (err) {
      console.error('Error looking up local geocode:', err);
      setIsLocalKamloopsAddress(false);
      setGeocodeStatus('error');
    } finally {
      setIsGeocoding(false);
    }
  };

  // Trigger OpenStreetMap address verification for Kamloops discount (debounced)
  useEffect(() => {
    if (!shippingCity.trim() && !shippingAddress.trim() && !shippingZip.trim()) {
      setIsLocalKamloopsAddress(false);
      setGeocodeStatus('idle');
      return;
    }

    const timer = setTimeout(() => {
      const targetCity = pricing.localDeliveryCityName || 'Kamloops';
      const effectiveCity = shippingCity.trim() || (shippingAddress.toLowerCase().includes(targetCity.toLowerCase()) ? targetCity : '');
      checkLocalShippingDiscount(shippingAddress, effectiveCity, shippingZip);
    }, 600); // 600ms debounce to prevent hitting Nominatim too aggressively!

    return () => clearTimeout(timer);
  }, [shippingCity, shippingAddress, shippingZip, pricing.localDeliveryCityName, pricing.localDeliveryPostalPrefixes]);

  // Sandbox card details emulated
  const [emulatedCardNumber, setEmulatedCardNumber] = useState('4242 •••• •••• 4242');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  // Initialize and check Stripe Redirect params from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    const orderId = params.get('orderId');

    if (paymentStatus === 'success' && orderId) {
      setIsFetchingOrderAfterCheckout(true);
      // Find order to display success screen
      const fetchOrderDetails = async () => {
        try {
          const res = await fetch(`/api/orders/${orderId}/public-success`);
          if (res.ok) {
            const data = await res.json();
            setPaymentSuccessOrder(data);
            setSelectedOrderTracking(data); // Highlight the paid order immediately in the workspace tracking card
            
            // Track Google Analytics Purchase
            trackPurchase({
              transaction_id: data.id,
              value: data.total,
              shipping: data.shippingCost || 0,
              items: (data.magnets || []).map((m: any) => ({
                id: m.id || 'custom_magnet',
                width: m.width || 3,
                height: m.height || 2,
                price: m.price || (data.subtotal / (data.magnets || []).length || 5.99),
                quantity: m.quantity || 1
              }))
            });

            setCart([]); // Clear Cart upon successful checkout
            clearDesignerDrafts();
            setActiveTab('dashboard');
            
            if (auth.token) {
              fetchOrderHistory(); // Sync registered customer order list
            } else {
              // Pre-populate lookup and directly display guest transit records in tracking pane
              setLookupOrderId(data.id);
              setLookupEmail(data.customerEmail);
              setGuestLookedUpOrder(data);
            }
            
            // Clean up address bar params dynamically without re-triggering
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (err) {
          console.error('Failed querying success order state', err);
        } finally {
          setIsFetchingOrderAfterCheckout(false);
        }
      };
      
      fetchOrderDetails();
    } else if (paymentStatus === 'cancelled') {
      setPaymentCancelled(true);
      setActiveTab('cart');
      setSelectedOrderTracking(null);
      setGuestLookedUpOrder(null);
      
      const orderId = params.get('orderId');
      if (orderId) {
        const fetchDraftOrder = async () => {
          try {
            const res = await fetch(`/api/orders/${orderId}/public-cancel`);
            if (res.ok) {
              const data = await res.json();
              if (data && data.magnets && data.magnets.length > 0) {
                setCart(data.magnets);
              }
            }
          } catch (err) {
            console.error('Failed restoring cart from draft order:', err);
          } finally {
            if (auth.token) {
              fetchOrderHistory();
            }
          }
        };
        fetchDraftOrder();
      }

      // Clean up address bar params dynamically without re-triggering
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [auth.token]);

  // Dismiss checkout persistent notification cards if user navigates to another page or tab
  useEffect(() => {
    if (paymentSuccessOrder && activeTab !== 'dashboard') {
      setPaymentSuccessOrder(null);
    }
    if (paymentCancelled && activeTab !== 'cart') {
      setPaymentCancelled(false);
    }
  }, [activeTab, paymentSuccessOrder, paymentCancelled]);

  const handleSessionExpired = (isAdmin: boolean = false) => {
    handleLogout();
    if (isAdmin) {
      setAuthModalMessage("Your administration session has expired due to inactivity. Please sign in again to access the admin console.");
    } else {
      setAuthModalMessage("Your login session has expired. Please sign in again to continue.");
    }
    setShowAuthModal(true);
  };

  // Sync Customer's Order History
  const fetchOrderHistory = async () => {
    if (!auth.token) return;
    setLoadingOrders(true);
    try {
      const response = await fetch('/api/orders', {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (response.status === 401 || response.status === 403) {
        handleSessionExpired(auth.user?.role === 'admin');
        return;
      }
      if (response.ok) {
        const data = await response.json();
        const validOrders = (Array.isArray(data) ? data : [])
          .filter((o: Order) => o.status !== 'pending' && o.status !== 'cancelled')
          .map((o: Order) => ({
            ...o,
            carrier: o.carrier === 'USPS' ? 'Canada Post' : o.carrier
          }));
        setOrderHistory(validOrders);
        
        // Sync currently opened order details in real-time
        if (selectedOrderTracking) {
          const updatedSelected = validOrders.find((o: Order) => o.id === selectedOrderTracking.id);
          if (updatedSelected) {
            setSelectedOrderTracking(updatedSelected);
          } else if (selectedOrderTracking.status === 'pending' || selectedOrderTracking.status === 'cancelled') {
            setSelectedOrderTracking(null);
          }
        }
      }
    } catch (err) {
      console.error('Failed compiling orders history', err);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchSavedDesigns = async () => {
    if (!auth.token) return;
    setLoadingSavedDesigns(true);
    try {
      const response = await fetch('/api/saved-designs', {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (response.status === 401 || response.status === 403) {
        handleSessionExpired(auth.user?.role === 'admin');
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setSavedDesigns(data);
      }
    } catch (err) {
      console.error('Failed loading saved designs', err);
    } finally {
      setLoadingSavedDesigns(false);
    }
  };

  const handleSaveDesign = (design: Omit<MagnetDesign, 'id'>, savedDesignId?: string) => {
    setDesignToSave(design);
    setEditingSavedDesignId(savedDesignId || null);
    setSaveAsNewCopy(false);
    if (!auth.token) {
      setShowAuthModal(true);
      return;
    }
    if (savedDesignId) {
      const existing = savedDesigns.find(sd => sd.id === savedDesignId);
      const nameToUse = existing ? existing.name : 'My Saved Magnet';
      setSaveDesignName(nameToUse);
      setShowSaveDesignModal(true);
    } else {
      setSaveDesignName('My Saved Magnet');
      setShowSaveDesignModal(true);
    }
  };

  const handleSaveDesignConfirm = async () => {
    if (!designToSave || !saveDesignName.trim()) return;
    setSavingDesignState(true);
    try {
      const response = await fetch('/api/saved-designs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`
        },
        body: JSON.stringify({
          id: (editingSavedDesignId && !saveAsNewCopy) ? editingSavedDesignId : undefined,
          name: saveDesignName.trim(),
          design: designToSave
        })
      });
      if (response.ok) {
        const saved = await response.json();
        setSavedDesigns(prev => {
          const exists = prev.some(x => x.id === saved.id);
          if (exists) {
            return prev.map(x => x.id === saved.id ? saved : x);
          } else {
            return [saved, ...prev];
          }
        });
        setEditingDesignId(saved.id);
        showToast(editingSavedDesignId && !saveAsNewCopy ? 'Design updated successfully!' : 'Design saved to My Designs!', 'success');
        setShowSaveDesignModal(false);
        setDesignToSave(null);
        setEditingSavedDesignId(null);
        setSaveAsNewCopy(false);
        setSaveDesignName('');
      } else {
        const err = await response.json();
        showToast(err.error || 'Failed to save design', 'error');
      }
    } catch (err) {
      console.error('Failed to save design:', err);
      showToast('An error occurred while saving the design.', 'error');
    } finally {
      setSavingDesignState(false);
    }
  };

  const handleDeleteSavedDesignConfirm = async () => {
    if (!designToDelete) return;
    setDeletingInProgress(true);
    try {
      const response = await fetch(`/api/saved-designs/${designToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${auth.token}`
        }
      });
      if (response.ok) {
        setSavedDesigns(prev => prev.filter(x => x.id !== designToDelete.id));
        setDesignToDelete(null);
        showToast('Design deleted successfully.', 'success');
      } else {
        const err = await response.json();
        showToast(err.error || 'Failed to delete saved design', 'error');
      }
    } catch (err) {
      console.error('Failed to delete saved design:', err);
      showToast('An error occurred while deleting the design.', 'error');
    } finally {
      setDeletingInProgress(false);
    }
  };

  const handleRenameSavedDesignConfirm = async () => {
    if (!designToRename || !renameInputName.trim()) return;
    setRenamingInProgress(true);
    try {
      const response = await fetch('/api/saved-designs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`
        },
        body: JSON.stringify({
          id: designToRename.id,
          name: renameInputName.trim(),
          design: designToRename.design
        })
      });
      if (response.ok) {
        const saved = await response.json();
        setSavedDesigns(prev => prev.map(x => x.id === designToRename.id ? saved : x));
        setDesignToRename(null);
        setRenameInputName('');
        showToast('Design renamed successfully!', 'success');
      } else {
        const err = await response.json();
        showToast(err.error || 'Failed to rename design', 'error');
      }
    } catch (err) {
      console.error('Failed to rename design:', err);
      showToast('An error occurred while renaming.', 'error');
    } finally {
      setRenamingInProgress(false);
    }
  };

  const handleInlineRename = async (id: string, currentDesign: MagnetDesign, newName: string) => {
    if (!newName.trim()) return;
    setInlineRenamingInProgress(id);
    try {
      const response = await fetch('/api/saved-designs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`
        },
        body: JSON.stringify({
          id: id,
          name: newName.trim(),
          design: currentDesign
        })
      });
      if (response.ok) {
        const saved = await response.json();
        setSavedDesigns(prev => prev.map(x => x.id === id ? saved : x));
        setEditingSavedDesignIdForInline(null);
        showToast('Design renamed successfully!', 'success');
      } else {
        const err = await response.json();
        showToast(err.error || 'Failed to rename design', 'error');
      }
    } catch (err) {
      console.error('Failed to inline rename design:', err);
      showToast('An error occurred while renaming.', 'error');
    } finally {
      setInlineRenamingInProgress(null);
    }
  };

  useEffect(() => {
    if (auth.token) {
      // Load from localStorage cache immediately so user doesn't wait for API response
      try {
        if (auth.user) {
          const cachedOrders = localStorage.getItem(`magnet_orders_${auth.user.id}`);
          if (cachedOrders) {
            setOrderHistory(JSON.parse(cachedOrders));
          }
          const cachedDesigns = localStorage.getItem(`magnet_saved_designs_${auth.user.id}`);
          if (cachedDesigns) {
            setSavedDesigns(JSON.parse(cachedDesigns));
          }
        }
      } catch (e) {
        console.warn('Failed to load cached data during auth change', e);
      }

      fetchOrderHistory();
      fetchSavedDesigns();
    } else {
      setOrderHistory([]);
      setSavedDesigns([]);
    }
  }, [auth.token, auth.user?.id]);

  // Automatically scroll to the top of the page when navigating between tabs/pages or viewing tracking details
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeTab, selectedOrderTracking]);

  // Synchronize browser URL query parameters dynamically with active tab and sub-tabs
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    let isDirectPath = true;
    let targetPath = '/';
    let queryPage = '';
    
    if (activeTab === 'news') {
      targetPath = selectedNewsSlug ? `/news/${selectedNewsSlug}` : '/news';
    } else if (isUseCaseSlug(activeTab)) {
      targetPath = `/${activeTab}`;
    } else if (activeTab === 'fundraising') {
      targetPath = '/fundraising';
    } else if (activeTab === 'giftcards') {
      targetPath = '/gift-cards';
    } else if (activeTab === 'shop') {
      targetPath = '/studio';
    } else if (activeTab === 'moments') {
      targetPath = '/moments';
    } else if (activeTab === 'cart') {
      targetPath = '/cart';
    } else if (activeTab === 'dashboard') {
      if (dashboardTab === 'saved_designs') {
        isDirectPath = false;
        queryPage = 'saved_designs';
      } else {
        targetPath = '/track';
      }
    } else if (activeTab === 'browse') {
      targetPath = '/browse';
    } else if (activeTab === 'privacy') {
      targetPath = '/privacy';
    } else if (activeTab === 'terms') {
      targetPath = '/terms';
    } else if (activeTab === 'admin') {
      isDirectPath = false;
      queryPage = 'admin';
    } else {
      targetPath = '/';
    }

    const params = new URLSearchParams(window.location.search);
    
    // Clean up tab/page query params if on a dedicated route
    if (isDirectPath) {
      params.delete('page');
      params.delete('tab');
      // Clean up premade params when on non-browse tabs
      if (activeTab !== 'browse') {
        params.delete('premade');
        params.delete('premade_id');
        params.delete('premadeDesign');
      }
      const searchStr = params.toString();
      const newUrl = `${targetPath}${searchStr ? '?' + searchStr : ''}${window.location.hash}`;
      if (window.location.pathname !== targetPath) {
        window.history.pushState({ page: activeTab, slug: selectedNewsSlug }, '', newUrl);
      }
    } else {
      const currentTabParam = params.get('page') || params.get('tab');
      let currentNormalized = currentTabParam ? currentTabParam.toLowerCase().replace(/[-_]/g, '') : '';
      let targetNormalized = queryPage.toLowerCase().replace(/[-_]/g, '');

      if (currentNormalized !== targetNormalized || window.location.pathname !== '/') {
        if (queryPage === '') {
          params.delete('page');
          params.delete('tab');
        } else {
          params.set('page', queryPage);
          params.delete('tab');
        }
        const searchStr = params.toString();
        const newUrl = `/${searchStr ? '?' + searchStr : ''}${window.location.hash}`;
        window.history.pushState({ page: queryPage }, '', newUrl);
      }
    }
  }, [activeTab, dashboardTab, selectedNewsSlug]);

  // Support full browser Back & Forward navigation via popstate event handling
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handlePopState = () => {
      const { active, dashboard, newsSlug } = parseInitialUrlParams();
      setActiveTab(active);
      setDashboardTab(dashboard);
      setSelectedNewsSlug(newsSlug);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleCopyPageLink = () => {
    try {
      const url = window.location.href;
      navigator.clipboard.writeText(url);
      showToast('Copied direct link to clipboard! You can now share it with others.', 'success');
    } catch (e) {
      console.error(e);
      showToast('Failed to copy link automatically. Please copy the URL from your address bar.', 'error');
    }
  };

  // Authentication callbacks
  const handleProfileSuccess = (newToken: string, newUser: User) => {
    localStorage.setItem('magnet_token', newToken);
    localStorage.setItem('magnet_user', JSON.stringify(newUser));
    setAuth({ token: newToken, user: newUser });
    if (newUser.name) {
      setShippingName(newUser.name);
    }
  };

  const handleAuthSuccess = (newToken: string, newUser: User) => {
    localStorage.setItem('magnet_token', newToken);
    localStorage.setItem('magnet_user', JSON.stringify(newUser));
    setAuth({ token: newToken, user: newUser });
    setShowAuthModal(false);

    if (newUser.name && !shippingName) {
      setShippingName(newUser.name);
    }

    if (designToSave) {
      setSaveDesignName('My Saved Magnet');
      setShowSaveDesignModal(true);
    } else {
      // Default redirect
      if (newUser.role === 'admin') {
        setActiveTab('admin');
      } else if (activeTab === 'cart') {
        // Keep on checkout screen
        setActiveTab('cart');
      } else {
        setActiveTab('shop');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('magnet_token');
    localStorage.removeItem('magnet_user');
    setAuth({ token: null, user: null });
    setActiveTab('home');
    setCart([]);
    clearDesignerDrafts();
    setSelectedOrderTracking(null);
    setPaymentSuccessOrder(null);
    setDashboardTab('orders');
  };

  // Add customized design from crop workspace into cart state
  const handleAddDesignToCart = (design: Omit<MagnetDesign, 'id'> & { id?: string }) => {
    setCart(prev => {
      // 1. If we are editing, first match by the exact editingDesignId or design.id
      const targetId = design.id || editingDesignId;
      if (targetId) {
        const existingIdx = prev.findIndex(item => item.id === targetId);
        if (existingIdx !== -1) {
          const updated = [...prev];
          updated[existingIdx] = {
            ...updated[existingIdx],
            ...design,
            id: targetId,
          };
          return updated;
        }
      }

      // 2. Fallback to matching image url
      const existingIdx = prev.findIndex(item => item.imageUrl === design.imageUrl && (!!item.isMomentsTemplate) === (!!design.isMomentsTemplate));
      if (existingIdx !== -1) {
        const updated = [...prev];
        updated[existingIdx] = {
          ...updated[existingIdx],
          ...design,
        };
        return updated;
      } else {
        const newDesign: MagnetDesign = {
          ...design,
          id: 'mag_' + Math.random().toString(36).substr(2, 9)
        };
        return [...prev, newDesign];
      }
    });

    // GA Event Trackers
    const sizeWidth = design.width || 3;
    const sizeHeight = design.height || 2;
    const itemQty = design.quantity || 1;
    trackAddToCart({
      id: design.id || 'new_magnet',
      width: sizeWidth,
      height: sizeHeight,
      quantity: itemQty,
      price: getUnitPriceForQuantity(itemQty),
    });
    trackMagnetCustomizationComplete(sizeWidth, sizeHeight, design.filter || 'none');
    if (design.text && design.text.text && design.text.text.trim()) {
      trackTextAdded(
        design.text.text.trim().length,
        design.text.color || '#ffffff',
        design.text.fontFamily || 'sans-serif'
      );
    }

    setEditingDesignId(null);
    setActiveTab('cart');
  };

  // Add multiple custom designs into cart state at once
  const handleAddMultipleDesignsToCart = (designs: MagnetDesign[]) => {
    setCart(prev => {
      // Merge batch into existing cart without deleting other cart items
      const updated = [...prev];
      designs.forEach(newDesign => {
        const existingIdx = updated.findIndex(item => item.id === newDesign.id || (item.imageUrl === newDesign.imageUrl && !item.isMomentsTemplate && !newDesign.isMomentsTemplate));
        if (existingIdx !== -1) {
          updated[existingIdx] = {
            ...updated[existingIdx],
            ...newDesign
          };
        } else {
          updated.push(newDesign);
        }
      });
      return updated;
    });
    
    // GA Event Trackers for multiple additions
    designs.forEach(design => {
      const sizeWidth = design.width || 3;
      const sizeHeight = design.height || 2;
      const itemQty = design.quantity || 1;
      trackAddToCart({
        id: design.id || 'new_magnet',
        width: sizeWidth,
        height: sizeHeight,
        quantity: itemQty,
        price: getUnitPriceForQuantity(itemQty),
      });
      trackMagnetCustomizationComplete(sizeWidth, sizeHeight, design.filter || 'none');
      if (design.text && design.text.text && design.text.text.trim()) {
        trackTextAdded(
          design.text.text.trim().length,
          design.text.color || '#ffffff',
          design.text.fontFamily || 'sans-serif'
        );
      }
    });

    setEditingDesignId(null);
    setActiveTab('cart');
  };

  // Reopen a cart design in the editor
  const handleEditCartItem = (id: string) => {
    setEditingDesignId(id);
    const item = cart.find(c => c.id === id);
    if (item && item.isMomentsTemplate) {
      setActiveTab('moments');
    } else {
      setActiveTab('shop');
    }
  };

  // Open a pre-made design in the customizer / studio canvas
  const handleCustomizePremade = (design: PremadeDesign, quantity: number) => {
    const newItem: YourImageItem = {
      id: 'mag_pm_custom_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6),
      imageUrl: design.imageUrl,
      croppedImageUrl: design.imageUrl,
      crop: { x: 50, y: 50, width: 1.0, height: 1.0, rotation: 0 },
      filter: 'none',
      text: {
        text: '',
        fontSize: 18,
        color: '#ffffff',
        fontFamily: 'Inter',
        yPosition: 50,
        xPosition: 50,
        isBold: true,
        hasShadow: true,
      },
      additionalTexts: [],
      qrCode: { url: '', size: 25, xPosition: 15, yPosition: 15 },
      quantity: quantity || 1,
      backgroundColor: '#ffffff',
      originalImageUrl: design.imageUrl,
      overlayImageUrl: '',
      overlayOriginalImageUrl: '',
      overlayCrop: { x: 50, y: 50, width: 1.0, height: 1.0, rotation: 0 },
      overlayFilter: 'none',
      overlayIsActive: false,
      layerOrder: 'overlay-top',
      magnetFilterColor: '#000000',
      magnetFilterOpacity: 0,
      isPoorResolution: false,
      acceptedPoorResolution: true,
    };
    setCustomDesignToLoad(newItem);
    setEditingDesignId(null);
    setActiveTab('shop');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Remove element from Cart
  const handleRemoveFromCart = (id: string) => {
    setCart(prev => prev.filter(c => c.id !== id));
    removeDesignerDraftById(id);
  };

  // Update quantity of an item inside the Cart
  const handleUpdateCartItemQuantity = (id: string, qty: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        return { ...item, quantity: Math.max(0, qty) };
      }
      return item;
    }));
  };

  // Update NFC tag settings for an item inside the Cart
  const handleUpdateCartItemNfc = (id: string, nfcEnabled: boolean, nfcData?: string, nfcOption?: string) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        return {
          ...item,
          nfcEnabled,
          nfcData: nfcData !== undefined ? nfcData : item.nfcData,
          nfcOption: nfcOption !== undefined ? nfcOption : item.nfcOption
        };
      }
      return item;
    }));
  };

  // Calculations
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const unitPrice = getUnitPriceForQuantity(cartItemCount);
  const cartSubtotal = cart.reduce((sum, item) => {
    const basePrice = (item.isPremade && item.priceOverride !== undefined && item.priceOverride !== null)
      ? item.priceOverride
      : unitPrice;
    const nfcCost = item.nfcEnabled ? 1.50 : 0;
    return sum + (item.quantity * (basePrice + nfcCost));
  }, 0);
  
  const minSlabPrice = (pricing.scaledDiscounts && pricing.scaledDiscounts.length > 0)
    ? Math.min(...pricing.scaledDiscounts.map(d => d.pricePerMagnet), pricing.pricePerMagnet)
    : pricing.pricePerMagnet;
  const maxDiscountPercent = pricing.pricePerMagnet > 0
    ? Math.round(((pricing.pricePerMagnet - minSlabPrice) / pricing.pricePerMagnet) * 100)
    : 35;
  
  const isLocalPromoApplied = !!(pricing.enableKamloopsDiscount && isLocalKamloopsAddress);
  const isFreeShippingAchieved = pricing.enableFreeShipping || (cartSubtotal >= pricing.freeShippingThreshold);
  const standardShippingCost = isFreeShippingAchieved ? 0 : (cartSubtotal === 0 ? 0 : pricing.shippingRate);
  let shippingCost = cartSubtotal === 0 
    ? 0 
    : (isFreeShippingAchieved ? 0 : (isLocalPromoApplied ? (pricing.kamloopsShippingRate ?? 0) : standardShippingCost));

  if (appliedCoupon && appliedCoupon.isFundraiser && appliedCoupon.fundraiserAllowFreeSchoolDelivery && fundraiserDeliveryMethod === 'school') {
    shippingCost = 0;
  } else if (appliedCoupon && appliedCoupon.hasShippingOverride && appliedCoupon.shippingPriceOverride !== undefined && appliedCoupon.shippingPriceOverride !== null) {
    shippingCost = Number(appliedCoupon.shippingPriceOverride);
  }

  // Coupon / Digital Gift Card Discount Calculation
  let couponDiscountAmount = 0;
  if (appliedCoupon) {
    if (appliedCoupon.isGiftCard) {
      const availableBalance = Number(appliedCoupon.remainingBalance ?? appliedCoupon.discountValue ?? 0);
      couponDiscountAmount = Math.min(availableBalance, cartSubtotal + shippingCost);
    } else {
      const minM = appliedCoupon.minMagnets;
      const isMinValid = minM === undefined || minM === null || cartItemCount >= minM;

      if (isMinValid) {
        if (appliedCoupon.discountType === 'percentage') {
          couponDiscountAmount = cartSubtotal * (appliedCoupon.discountValue / 100);
        } else if (appliedCoupon.discountType === 'percentage_cap') {
          couponDiscountAmount = cartSubtotal * (appliedCoupon.discountValue / 100);
          if (appliedCoupon.maxDiscount && couponDiscountAmount > appliedCoupon.maxDiscount) {
            couponDiscountAmount = appliedCoupon.maxDiscount;
          }
        } else if (appliedCoupon.discountType === 'fixed_amount') {
          couponDiscountAmount = appliedCoupon.discountValue;
        } else if (appliedCoupon.discountType === 'buy_x_get_y') {
          const buyQty = appliedCoupon.buyQty || 1;
          const freeQty = appliedCoupon.freeQty || 1;
          const groupSize = buyQty + freeQty;
          const groups = Math.floor(cartItemCount / groupSize);
          const freeCount = groups * freeQty;
          couponDiscountAmount = freeCount * unitPrice;
        } else if (appliedCoupon.discountType === 'fixed_item_price') {
          const targetPrice = appliedCoupon.discountValue;
          if (unitPrice > targetPrice) {
            let eligibleQty = cartItemCount;
            if (appliedCoupon.maxMagnets !== undefined && appliedCoupon.maxMagnets !== null && appliedCoupon.maxMagnets > 0) {
              eligibleQty = Math.min(cartItemCount, appliedCoupon.maxMagnets);
            }
            couponDiscountAmount = eligibleQty * (unitPrice - targetPrice);
          } else {
            couponDiscountAmount = 0;
          }
        }

        // Apply generic max discount cap limit across percentage or fixed_item_price if specified
        if (appliedCoupon.maxDiscount && couponDiscountAmount > appliedCoupon.maxDiscount) {
          couponDiscountAmount = appliedCoupon.maxDiscount;
        }
      } else {
        couponDiscountAmount = 0;
      }

      if (couponDiscountAmount > cartSubtotal) {
        couponDiscountAmount = cartSubtotal;
      }
    }
  }

  const finalDiscount = Number(couponDiscountAmount.toFixed(2));
  const cartTotal = appliedCoupon?.isGiftCard
    ? Number(Math.max(0, cartSubtotal + shippingCost - finalDiscount).toFixed(2))
    : Number(Math.max(0, cartSubtotal - finalDiscount + shippingCost).toFixed(2));
 
  // Google Analytics Page-view, Customizer, and Checkout Begin Trackers
  useEffect(() => {
    if (activeTab === 'shop' || activeTab === 'moments') {
      trackCustomizerOpen();
    } else if (activeTab === 'cart' && cart.length > 0) {
      trackBeginCheckout(cart, cartSubtotal);
    }
  }, [activeTab]);

  // Trigger promo event when Kamloops address promo is resolved and applied
  useEffect(() => {
    if (isLocalPromoApplied) {
      const discountSaved = Math.max(0, standardShippingCost - (pricing.kamloopsShippingRate ?? 0));
      trackPromoCodeApplied('KAMLOOPS_LOCAL_PROMO', discountSaved);
    }
  }, [isLocalPromoApplied]);
  
  // Handle Gust Track Lookup API
  const handleGuestLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLookupError('');
    setLookupLoading(true);
    setGuestLookedUpOrder(null);

    try {
      const res = await fetch('/api/orders/lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderId: lookupOrderId.trim(),
          email: lookupEmail.trim()
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to lookup order');
      }

      setGuestLookedUpOrder(data);
      setSelectedOrderTracking(data);
    } catch (err: any) {
      setLookupError(err.message || 'Error occurred during lookup.');
    } finally {
      setLookupLoading(false);
    }
  };

  // Handle Checkout submission (supports standard and 1-tap express checkout)
  const handleCheckoutSubmit = async (e?: React.FormEvent, isExpress: boolean = false) => {
    if (e && e.preventDefault) e.preventDefault();

    if (cart.length === 0) {
      setCheckoutError('Please add at least one magnet design to your order first.');
      return;
    }

    if (!isExpress) {
      if (!shippingName.trim() || !guestEmail.trim()) {
        setCheckoutError('Please populate the recipient name and your billing email address for order registration.');
        return;
      }

      if (!shippingAddress.trim() || !shippingCity.trim() || !shippingZip.trim()) {
        setCheckoutError('Please populate all shipping address fields safely.');
        return;
      }

      if (ALLOW_ONLY_CANADA_SHIPPING && shippingCountry === 'Canada') {
        const cleanZip = shippingZip.trim().replace(/\s+/g, '');
        const canadaPostalRegex = /^[A-Za-z]\d[A-Za-z]\d[A-Za-z]\d$/;
        if (!canadaPostalRegex.test(cleanZip)) {
          setCheckoutError('Please enter a valid Canadian postal code (e.g., V2C 0A1) for delivery within Canada.');
          return;
        }
      }

      if (!billingSameAsShipping) {
        if (!billingName.trim()) {
          setCheckoutError('Please enter the name on your billing card / address.');
          return;
        }
        if (!billingAddress.trim() || !billingCity.trim() || !billingZip.trim()) {
          setCheckoutError('Please populate all billing address fields.');
          return;
        }
        if (ALLOW_ONLY_CANADA_SHIPPING && billingCountry === 'Canada') {
          const cleanBillingZip = billingZip.trim().replace(/\s+/g, '');
          const canadaPostalRegex = /^[A-Za-z]\d[A-Za-z]\d[A-Za-z]\d$/;
          if (!canadaPostalRegex.test(cleanBillingZip)) {
            setCheckoutError('Please enter a valid Canadian postal code (e.g., V2C 0A1) for your billing address.');
            return;
          }
        }
      }
    }

    setIsProcessingPayment(true);
    setCheckoutError('');

    try {
      // 1. Create Checkout Order Payload on DB
      const isExpressOrder = Boolean(isExpress);
      const effectiveName = shippingName.trim() || (auth.user?.displayName || auth.user?.name || (isExpressOrder ? 'Express Checkout Customer' : 'Customer'));
      const effectiveEmail = guestEmail.trim() || (auth.user?.email || '');
      const effectiveAddress = shippingAddress.trim() || (isExpressOrder ? 'Express Checkout (Address provided by Apple Pay / Google Pay)' : 'Pending Customer Address');
      const effectiveCity = shippingCity.trim() || (isLocalKamloopsAddress ? 'Kamloops' : (isExpressOrder ? '' : ''));
      const effectiveZip = shippingZip.trim() || (isLocalKamloopsAddress ? 'V2C 1A1' : (isExpressOrder ? '' : ''));

      const resOrder = await fetch('/api/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(isExpressOrder ? { 'x-checkout-mode': 'express' } : {}),
          ...(auth.token ? { Authorization: `Bearer ${auth.token}` } : {})
        },
        body: JSON.stringify({
          magnets: cart.map(item => {
            const clean = { ...item };
            // Ensure payload is compact and stays well within Cloud Run/network limits
            if (clean.croppedImageUrl && clean.imageUrl && clean.imageUrl.length > 500000) {
              clean.imageUrl = clean.croppedImageUrl;
            }
            if (clean.originalImageUrl && clean.originalImageUrl.length > 500000) {
              clean.originalImageUrl = clean.croppedImageUrl || clean.imageUrl;
            }
            return clean;
          }),
          subtotal: cartSubtotal,
          shippingCost: shippingCost,
          total: cartTotal,
          isExpress: isExpressOrder,
          shippingAddress: effectiveAddress,
          shippingCity: effectiveCity,
          shippingZip: effectiveZip,
          shippingPostalCode: effectiveZip,
          shippingCountry: shippingCountry.trim() || 'Canada',
          billingSameAsShipping,
          billingName: (billingSameAsShipping ? effectiveName : (billingName.trim() || effectiveName)),
          billingAddress: (billingSameAsShipping ? effectiveAddress : (billingAddress.trim() || effectiveAddress)),
          billingCity: (billingSameAsShipping ? effectiveCity : (billingCity.trim() || effectiveCity)),
          billingZip: (billingSameAsShipping ? effectiveZip : (billingZip.trim() || effectiveZip)),
          billingPostalCode: (billingSameAsShipping ? effectiveZip : (billingZip.trim() || effectiveZip)),
          billingCountry: (billingSameAsShipping ? shippingCountry : billingCountry).trim() || 'Canada',
          customerPhone: customerPhone.trim(),
          customerName: effectiveName,
          customerEmail: effectiveEmail,
          isGift: isGift,
          giftMessage: isGift ? giftMessage : '',
          giftTo: isGift ? giftTo : '',
          giftFrom: isGift ? giftFrom : '',
          specialInstructions: specialInstructions.trim() ? specialInstructions : undefined,
          allowSocialMediaFeatured: allowSocialMediaFeatured,
          couponCode: appliedCoupon ? appliedCoupon.code : undefined,
          discountAmount: finalDiscount || undefined,
          fundraiserStudentName: (appliedCoupon && appliedCoupon.isFundraiser) ? (fundraiserStudentName.trim() || undefined) : undefined,
          fundraiserClassroom: (appliedCoupon && appliedCoupon.isFundraiser) ? (fundraiserClassroom.trim() || undefined) : undefined,
          fundraiserDeliveryMethod: (appliedCoupon && appliedCoupon.isFundraiser) ? fundraiserDeliveryMethod : undefined
        })
      });

      const resOrderText = await resOrder.text();
      let orderData: any = null;
      try {
        orderData = resOrderText ? JSON.parse(resOrderText) : null;
      } catch (parseErr) {
        console.error('Non-JSON server response from /api/orders:', resOrder.status, resOrderText);
      }

      if (!resOrder.ok) {
        if (resOrder.status === 503 || resOrder.status === 504 || resOrder.status === 502) {
          throw new Error('Our ordering service is temporarily busy. Please wait a moment and try placing your order again.');
        }
        throw new Error(orderData?.error || `Order creation failed (HTTP ${resOrder.status}). Please try again.`);
      }

      if (!orderData || !orderData.id) {
        throw new Error('Failed saving draft order. Please try again.');
      }

      // 2. Initialise Secure Stripe checkout / Sandbox simulation
      const resPay = await fetch(`/api/orders/${orderData.id}/pay`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(auth.token ? { Authorization: `Bearer ${auth.token}` } : {})
        }
      });

      const resPayText = await resPay.text();
      let payData: any = null;
      try {
        payData = resPayText ? JSON.parse(resPayText) : null;
      } catch (parseErr) {
        console.error('Non-JSON server response from /api/orders/:id/pay:', resPay.status, resPayText);
      }

      if (!resPay.ok) {
        if (resPay.status === 503 || resPay.status === 504 || resPay.status === 502) {
          throw new Error('The payment gateway is momentarily initializing. Please try again in a few moments.');
        }
        throw new Error(payData?.error || `Payment setup failed (HTTP ${resPay.status}). Please try again.`);
      }

      if (!payData) {
        throw new Error('Failed payments processing. Please try again.');
      }

      // Stripe Session link checkout redirect
      if (payData.checkoutUrl) {
        window.location.href = payData.checkoutUrl; // Relocate client tab to actual Stripe Payment Frame!
      } else {
        // Mock Sandbox Payment Success
        const orderObj = payData.order;
        trackPurchase({
          transaction_id: orderObj.id,
          value: orderObj.total,
          shipping: orderObj.shippingCost || 0,
          items: (orderObj.magnets || []).map((m: any) => ({
            id: m.id || 'custom_magnet',
            width: m.width || 3,
            height: m.height || 2,
            price: m.price || (orderObj.subtotal / (orderObj.magnets || []).length || 5.99),
            quantity: m.quantity || 1
          }))
        });

        setPaymentSuccessOrder(payData.order);
        setCart([]); // Reset Cart
        clearDesignerDrafts();
        setActiveTab('dashboard');
        
        if (auth.token) {
          // Refresh orders list
          fetchOrderHistory();
        } else {
          // Pre-populate lookup and directly display guest transit records in tracking pane
          setLookupOrderId(payData.order.id);
          setLookupEmail(payData.order.customerEmail);
          setGuestLookedUpOrder(payData.order);
          setSelectedOrderTracking(payData.order);
        }
      }

    } catch (err: any) {
      setCheckoutError(err.message || 'Error occurred handling payment secure handshake.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Express 1-Tap checkout trigger (Apple Pay / Google Pay)
  const handleExpressCheckout = async () => {
    await handleCheckoutSubmit(undefined, true);
  };

  const getCarrierTrackingUrl = (carrier?: string, trackingNumber?: string): string | null => {
    const rawTracking = (trackingNumber || '').trim();
    if (!rawTracking) return null;
    const lowerTracking = rawTracking.toLowerCase();
    if (
      lowerTracking === 'hand-delivery' ||
      lowerTracking === 'hand-delivered' ||
      lowerTracking.includes('standard delivery') ||
      lowerTracking.includes('no tracking')
    ) {
      return null;
    }

    const effectiveCarrier = (!carrier || carrier === 'USPS') ? 'Canada Post' : carrier.trim();
    const lowerCarrier = effectiveCarrier.toLowerCase();

    // D5Courier does not have online tracking
    if (lowerCarrier.includes('d5')) {
      return null;
    }

    const pin = encodeURIComponent(rawTracking);

    if (lowerCarrier.includes('canada post') || lowerCarrier === 'canada_post') {
      return `https://www.canadapost-postescanada.ca/track-reperage/en#/details/${pin}`;
    }
    if (lowerCarrier.includes('canpar')) {
      return `https://www.canpar.com/en/tracking/track.htm?barcode=${pin}`;
    }
    if (lowerCarrier.includes('purolator')) {
      return `https://www.purolator.com/en/shipping/tracker?pins=${pin}`;
    }
    if (lowerCarrier.includes('ics')) {
      return `https://www.icscourier.ca/online-services/parcel-tracking.aspx?pin=${pin}`;
    }
    if (lowerCarrier.includes('loomis')) {
      return `https://www.loomisexpress.com/loomship/Track/TrackStatus?barcode=${pin}&keysearch=${pin}`;
    }
    if (lowerCarrier.includes('fedex')) {
      return `https://www.fedex.com/fedextrack/?trknbr=${pin}`;
    }
    if (lowerCarrier.includes('ups')) {
      return `https://www.ups.com/track?tracknum=${pin}`;
    }
    if (lowerCarrier.includes('dhl')) {
      return `https://www.dhl.com/ca-en/home/tracking.html?tracking-id=${pin}&submit=1`;
    }
    if (lowerCarrier.includes('usps')) {
      return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${pin}`;
    }

    return null;
  };

  const renderTrackingCard = (order: Order) => {
    const effectiveCarrier = (!order.carrier || order.carrier === 'USPS') ? 'Canada Post' : order.carrier;
    const trackingUrl = getCarrierTrackingUrl(effectiveCarrier, order.trackingNumber);

    return (
      <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm space-y-5 animate-scaleIn shadow-all" id="shipment-tracking-frame">
        {/* Tracking Header details */}
        <div className="border-b border-slate-100 pb-4">
          <div className="flex justify-between items-start">
            <div>
              <span className="text-[10px] font-extrabold tracking-wider uppercase text-orange-600">
                {stepConfigs.find(c => c.id === 'transit_status_label')?.title || 'Order Details'}
              </span>
              <h3 className="text-sm font-extrabold text-slate-900 mt-0.5 font-mono">{order.id}</h3>
            </div>
            <span className={`px-2 py-0.5 rounded-full border text-[9px] font-extrabold uppercase ${
              {
                pending: 'bg-slate-50 text-slate-600 border-slate-200',
                paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                completed: 'bg-indigo-50 text-indigo-700 border-indigo-200',
                shipped: 'bg-orange-50 text-orange-700 border-orange-200',
                delivered: 'bg-sky-50 text-sky-700 border-sky-200',
                refunded: 'bg-rose-50 text-rose-700 border-rose-200',
              }[order.status] || 'bg-slate-100 border-slate-200'
            }`}>
              {mapStatusToLabel(order.status)}
            </span>
          </div>

          {(order.carrier || order.trackingNumber || order.status === 'shipped' || order.status === 'delivered') ? (
            <>
              <div className="mt-3 p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between gap-3">
                <div>
                  <span className="text-[9px] text-slate-500 block font-bold leading-none">Courier Shipped Carrier</span>
                  <span className="text-xs text-slate-800 block font-bold mt-1 uppercase tracking-wider">
                    {effectiveCarrier}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-[9px] text-slate-500 block font-bold leading-none">Tracking Number</span>
                  <span className="text-xs text-orange-600 font-mono font-bold block mt-1 select-all">
                    {order.trackingNumber || 'Standard Delivery (No Tracking #)'}
                  </span>
                  {trackingUrl && (
                    <a
                      href={trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-end gap-1 text-[10px] font-extrabold text-indigo-600 hover:text-indigo-800 underline underline-offset-2 mt-1 transition-colors"
                    >
                      <span>Track on {effectiveCarrier}</span>
                      <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                    </a>
                  )}
                </div>
              </div>
              <p className="text-slate-500 text-[11px] mt-2.5 leading-relaxed font-semibold">
                {stepConfigs.find(c => c.id === 'tracking_subtext')?.description || 'Your custom magnet order has shipped and is on its way to you!'}
              </p>
            </>
          ) : (
            <p className="text-slate-500 text-[11px] mt-2.5 leading-relaxed font-semibold">
              {stepConfigs.find(c => c.id === 'no_tracking_subtext')?.description || 'Order is currently in printing, professional 30-mil backing molding, and high-gloss protective lamination wrapper process. Direct track links compile immediately upon carriage dispatch.'}
            </p>
          )}
        </div>

        {/* Paid Invoice Copy (PDF Download) - Only shown for paid/completed/shipped/delivered/refunded orders */}
        {order.status !== 'pending' && order.status !== 'cancelled' && (
          <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-4 flex items-center justify-between gap-3 animate-fadeIn shadow-xs" id={`invoice-card-${order.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100/60 rounded-xl flex items-center justify-center text-emerald-700 shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-black text-slate-800 leading-none">
                  {order.status === 'refunded' ? 'Refunded Invoice Copy' : 'Paid Invoice & Receipt'}
                </p>
                <p className="text-[10px] text-slate-500 font-bold mt-1">Official proof of payment (PDF)</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => generateInvoicePDF(order)}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-[11px] uppercase tracking-wider rounded-xl shadow-sm hover:shadow-md transition active:scale-97 cursor-pointer flex items-center gap-1.5"
              title="Download PDF invoice receipt copy"
              id={`btn-download-invoice-${order.id}`}
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </button>
          </div>
        )}

        {/* Gift details display */}
        {order.isGift && (
          <div className="bg-rose-50/70 border border-rose-100 p-3.5 rounded-xl space-y-2 animate-fadeIn">
            <span className="text-[10px] uppercase font-black tracking-widest text-rose-700 block flex items-center gap-1 font-sans">
              <Gift className="w-3.5 h-3.5 text-rose-700" />
              GIFT ORDER SELECTED
            </span>
            {(order.giftTo || order.giftFrom) && (
              <div className="grid grid-cols-2 gap-2 text-xs font-sans text-slate-700 border-b border-rose-100 pb-2">
                {order.giftTo && (
                  <div>
                    <span className="font-bold text-slate-400 text-[9px] uppercase tracking-wider block">To</span>
                    <span className="font-extrabold text-rose-900">{order.giftTo}</span>
                  </div>
                )}
                {order.giftFrom && (
                  <div>
                    <span className="font-bold text-slate-400 text-[9px] uppercase tracking-wider block">From</span>
                    <span className="font-extrabold text-rose-900">{order.giftFrom}</span>
                  </div>
                )}
              </div>
            )}
            {order.giftMessage ? (
              <div className="space-y-1">
                <span className="font-bold text-slate-400 text-[9px] uppercase tracking-wider block">Gift Message</span>
                <p className="font-semibold text-xs leading-normal font-sans text-left italic text-rose-950 bg-white/70 p-2.5 rounded border border-rose-105">
                  "{order.giftMessage}"
                </p>
              </div>
            ) : (
              <p className="font-semibold text-xs leading-normal font-sans text-left text-rose-850">
                A custom gift presentation card is included with this shipment.
              </p>
            )}
          </div>
        )}

        {/* Fundraiser campaign details */}
        {order.fundraiserStudentName && (
          <div className="bg-amber-50/70 border border-amber-100 p-3.5 rounded-xl space-y-2 animate-fadeIn text-left">
            <span className="text-[10px] uppercase font-black tracking-widest text-amber-700 block flex items-center gap-1 font-sans">
              <GraduationCap className="w-3.5 h-3.5 text-amber-700" />
              School / Charity Fundraiser
            </span>
            <div className="grid grid-cols-2 gap-2 text-xs font-sans text-slate-700">
              <div>
                <span className="font-bold text-slate-400 text-[9px] uppercase tracking-wider block">Student / Child Name</span>
                <span className="font-extrabold text-amber-900">{order.fundraiserStudentName}</span>
              </div>
              <div>
                <span className="font-bold text-slate-400 text-[9px] uppercase tracking-wider block">Classroom / Grade</span>
                <span className="font-extrabold text-amber-900">{order.fundraiserClassroom || 'N/A'}</span>
              </div>
            </div>
            {order.couponCode && (
              <p className="text-[10px] text-slate-500 font-semibold border-t border-amber-100/40 pt-1.5 leading-normal">
                Campaign applied: <strong className="font-mono text-amber-800 font-extrabold">{order.couponCode}</strong>. {
                  (order.fundraiserDeliveryMethod === 'school' || !order.fundraiserDeliveryMethod)
                    ? 'We are coordinating free bulk delivery directly to the school location, '
                    : 'Your order will be shipped directly to the home address provided, '
                }and giving special thanks to <strong className="text-amber-900 font-black">{order.fundraiserStudentName}</strong> for their fundraising support!
              </p>
            )}
          </div>
        )}

        {/* Special Instructions display */}
        {order.specialInstructions && (
          <div className="bg-slate-50/70 border border-slate-200 p-3.5 rounded-xl space-y-1.5 animate-fadeIn">
            <span className="text-[10px] uppercase font-black tracking-widest text-slate-550 block flex items-center gap-1 font-sans">
              <FileText className="w-3.5 h-3.5 text-slate-500" />
              Special Instructions
            </span>
            <p className="font-semibold text-xs leading-relaxed font-sans text-left text-slate-800 bg-white/70 p-2.5 rounded border border-slate-200 whitespace-pre-wrap">
              {order.specialInstructions}
            </p>
          </div>
        )}

        {/* Social Media Featuring Permission display */}
        <div className={`p-3 rounded-xl border text-xs font-sans animate-fadeIn ${
          order.allowSocialMediaFeatured !== false
            ? 'bg-purple-50/70 border-purple-200/80 text-purple-900'
            : 'bg-slate-50 border-slate-200 text-slate-700'
        }`}>
          <span className="text-[10px] uppercase font-black tracking-wider block font-sans mb-1 text-purple-900 flex items-center gap-1.5">
            <Camera className="w-3.5 h-3.5" />
            Social Media Showcase Permission
          </span>
          <p className="font-semibold flex items-center gap-1.5">
            {order.allowSocialMediaFeatured !== false ? (
              <>
                <span className="bg-purple-600 text-white rounded-full w-4 h-4 inline-flex items-center justify-center text-[10px] font-black">
                  <Check className="w-2.5 h-2.5" />
                </span>
                You agreed to allow Square Magnets to feature your custom magnet photo on social media & showcase gallery!
              </>
            ) : (
              <>
                <span className="bg-slate-400 text-white rounded-full w-4 h-4 inline-flex items-center justify-center text-[10px] font-black">
                  <X className="w-2.5 h-2.5" />
                </span>
                You requested for your custom magnet photo to remain private (no social media features).
              </>
            )}
          </p>
        </div>

        {/* Design displays with intact individual item prices */}
        <div className="space-y-2">
          <span className="text-[9px] font-extrabold text-slate-450 uppercase tracking-wider block">Custom designs in package</span>
          <div className="space-y-1.5">
            {order.magnets.map((mag, idx) => {
              const itemPricing = getOrderItemPricing(order, mag);
              return (
                <div
                  key={idx}
                  className="p-2.5 rounded-xl border border-slate-200 bg-slate-50/70 space-y-1.5"
                >
                  <div className="flex items-center justify-between gap-2.5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <button
                        type="button"
                        onClick={() => setPreviewItem(mag as any)}
                        className="relative w-10 h-10 border border-slate-200 rounded-lg overflow-hidden shadow-xs hover:ring-2 hover:ring-orange-500 cursor-zoom-in transition-all shrink-0"
                        style={{ backgroundColor: mag.backgroundColor || '#ffffff' }}
                        title={`Click to zoom. Quantity: ${mag.quantity}`}
                      >
                        <img src={mag.croppedImageUrl} style={{ backgroundColor: mag.backgroundColor || '#ffffff' }} className="w-full h-full object-contain" referrerPolicy="no-referrer" draggable={false} />
                        <span className="absolute bottom-0 right-0 bg-slate-900/90 font-mono text-[7px] font-extrabold text-orange-400 px-1 rounded-tl">x{mag.quantity}</span>
                      </button>
                      <div className="min-w-0 text-left">
                        <p className="text-[11px] font-extrabold text-slate-900">
                          Design #{idx + 1} {mag.nfcEnabled ? '⚡ +NFC Tag' : ''}
                        </p>
                        <p className="text-[10px] text-slate-500 font-semibold">
                          Qty: {mag.quantity} &times; ${itemPricing.unitPrice.toFixed(2)}/ea
                          {mag.nfcEnabled ? ` ($${itemPricing.baseUnitPrice.toFixed(2)} + $1.50 NFC)` : ''}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs font-mono font-extrabold text-slate-900 shrink-0">
                      ${itemPricing.lineTotal.toFixed(2)}
                    </span>
                  </div>
                  {mag.text?.text && (
                    <p className="text-[10px] text-slate-700 italic whitespace-pre-wrap break-words text-left bg-white/80 px-2 py-1 rounded border border-slate-200/70">
                      Custom Text: "{mag.text.text}"
                    </p>
                  )}
                  {mag.nfcEnabled && (
                    <div className="text-[10px] text-teal-900 bg-teal-50/90 border border-teal-200/80 rounded-lg px-2 py-1.5 text-left space-y-0.5">
                      <span className="font-bold text-[#008080] block">
                        ⚡ NFC Tag ({mag.nfcOption || 'Programmed'}):
                      </span>
                      <span className="font-mono text-slate-800 break-all whitespace-pre-wrap block">
                        {mag.nfcData || 'No data provided'}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Timeline flow */}
        <div className="space-y-4">
          <span className="text-[9px] font-extrabold text-slate-450 uppercase tracking-wider block">Fulfillment Tracking Checkpoints</span>
          
          <div className="relative border-l border-slate-200 pl-4 ml-2 ml-2.5 space-y-5 py-1">
            {[...order.trackingHistory].reverse().map((step, idx) => (
              <div key={idx} className="relative animate-fadeIn">
                {/* Visual tracker dot */}
                <span className={`absolute -left-[21.5px] top-1 w-2.5 h-2.5 rounded-full border ring-4 ring-white 
                  ${idx === 0 ? 'bg-orange-500 border-orange-500 animate-ping' : 'bg-slate-300 border-slate-200'}`} 
                />
                <span className={`absolute -left-[21.5px] top-1 w-2.5 h-2.5 rounded-full border ring-4 ring-white 
                  ${idx === 0 ? 'bg-orange-500 border-orange-500' : 'bg-slate-300 border-slate-200'}`} 
                />

                <div className="text-xs">
                  <span className="font-extrabold text-slate-900 block">{step.status}</span>
                  <p className="text-slate-500 font-medium text-[11px] mt-0.5 leading-relaxed">{step.description}</p>
                  <span className="text-[9px] text-slate-400 block font-mono mt-1 font-semibold">
                    {new Date(step.timestamp).toLocaleDateString()} &bull; {new Date(step.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f7f7ef] text-[#082d48] font-sans flex flex-col">
      <svg xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
        <defs>
          <symbol id="arrow" viewBox="0 0 24 24"><path d="M4 12h15m-6-6 6 6-6 6" /></symbol>
          <symbol id="truck" viewBox="0 0 24 24"><path d="M2 5h12v12H2zM14 9h4l4 4v4h-8M16 9v5h6" /><circle cx="6" cy="18" r="2" /><circle cx="18" cy="18" r="2" /></symbol>
          <symbol id="leaf" viewBox="0 0 24 24"><path d="m12 2 2.2 5.3L17 5.5l-.5 5 4-1.3-1.3 4 2 1.3-7 3.4.6 3.1h-5.6l.6-3.1-7-3.4 2-1.3-1.3-4 4 1.3-.5-5 2.8 1.8L12 2Z" /></symbol>
          <symbol id="bag" viewBox="0 0 24 24"><path d="M5 7h14l1 14H4L5 7ZM8 7V5a4 4 0 0 1 8 0v2" /></symbol>
          <symbol id="user" viewBox="0 0 24 24"><circle cx="12" cy="7" r="4" /><path d="M4 21v-2a8 8 0 0 1 16 0v2" /></symbol>
          <symbol id="menu" viewBox="0 0 24 24"><path d="M3 6h18M3 12h18M3 18h18" /></symbol>
          <symbol id="check" viewBox="0 0 24 24"><path d="m5 12 4 4L19 6" /></symbol>
          <symbol id="shield" viewBox="0 0 24 24"><path d="m12 2 8 4v6c0 5-8 10-8 10S4 17 4 12V6l8-4Z" /><path d="m8 12 3 3 5-6" /></symbol>
          <symbol id="layers" viewBox="0 0 24 24"><path d="m12 2 10 5-10 5L2 7l10-5ZM2 12l10 5 10-5M2 17l10 5 10-5" /></symbol>
          <symbol id="sparkles" viewBox="0 0 24 24"><path d="m10 2 2.5 6.5L19 11l-6.5 2.5L10 20l-2.5-6.5L1 11l6.5-2.5L10 2ZM20 2v5m-2.5-2.5h5M20 17v5m-2.5-2.5h5" /></symbol>
          <symbol id="heart" viewBox="0 0 24 24"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0l-1 1-1-1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" /></symbol>
          <symbol id="nfc" viewBox="0 0 24 24"><rect x="2" y="5" width="8" height="15" rx="2" /><path d="M5 17h2m6-8a4 4 0 0 1 0 6m3-9a8 8 0 0 1 0 12m3-15a12 12 0 0 1 0 18" /></symbol>
        </defs>
      </svg>

      {/* UTILITY TOP BAR */}
      <div id="global-announcement-bar" className="utility sm-fresh-design">
        <div className="wrap utility-inner">
          <span>
            <svg className="icon"><use href="#truck" /></svg> A little joy, delivered.{' '}
            <strong>
              {pricing.enableFreeShipping
                ? 'Free Canada shipping on all orders today!'
                : `Free Canada shipping at $${(pricing.freeShippingThreshold ?? 75) % 1 === 0 ? (pricing.freeShippingThreshold ?? 75).toFixed(0) : (pricing.freeShippingThreshold ?? 75).toFixed(2)}+.`}
            </strong>
          </span>
          <div>
            <a
              href="/track"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('dashboard');
                setDashboardTab('orders');
                setSelectedOrderTracking(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Track your order
            </a>
            <a
              href="/news"
              onMouseEnter={preloadNews}
              onTouchStart={preloadNews}
              onFocus={preloadNews}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('news');
                setSelectedNewsSlug(null);
                setSelectedOrderTracking(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              The blog
            </a>
            <span>Handcrafted in Kamloops, BC</span>
          </div>
        </div>
      </div>

      {/* ACTIVE SCHOOL / CHARITY FUNDRAISER WELCOME BANNER */}
      {appliedCoupon && appliedCoupon.isFundraiser && (
        <div id="fundraiser-welcome-banner" className="bg-rose-50 border-b border-rose-100 py-3 px-4 relative z-40 shadow-inner">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3 text-left">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center text-rose-700 shrink-0">
                <GraduationCap className="w-6 h-6" />
              </div>
              <div>
                <h4 className="text-xs font-black text-rose-800 uppercase tracking-wide flex items-center gap-1.5">
                  Supporter Campaign Active: {appliedCoupon.fundraiserName}
                </h4>
                <p className="text-xs text-rose-950/70 font-semibold mt-0.5 max-w-2xl leading-normal">
                  {appliedCoupon.fundraiserDescription || 'Your purchase supports this school campaign!'} 
                  <span className="text-rose-600 font-extrabold ml-1">
                    {appliedCoupon.fundraiserContributionType === 'percentage'
                      ? `${appliedCoupon.fundraiserContributionValue}% of your purchase subtotal will be donated.`
                      : `$${appliedCoupon.fundraiserContributionValue?.toFixed(2)} for every magnet purchased will be donated.`}
                  </span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="px-2.5 py-1 bg-rose-100 text-rose-800 border border-rose-200 rounded-lg text-[10px] font-black uppercase tracking-wider">
                Code applied: {appliedCoupon.code}
              </span>
              <button
                type="button"
                onClick={handleRemoveCoupon}
                className="text-slate-400 hover:text-slate-600 text-xs font-bold transition flex items-center gap-1 p-1 cursor-pointer"
                title="Cancel Fundraiser Code Support"
              >
                <X className="w-3.5 h-3.5" /> Opt-out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SITE HEADER */}
      <header className="site-header sm-fresh-design">
        <div className="wrap">
          <div className="header-row">
            <a
              id="brand-logo-btn"
              className="brand"
              href="./"
              aria-label="SquareMagnets home"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('home');
                setSelectedOrderTracking(null);
                setIsMobileMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <img
                src="/squaremagnets-logo.webp"
                alt="SquareMagnets.ca — Custom Magnets. BIG Impact."
                width="1293"
                height="299"
                fetchPriority="high"
                referrerPolicy="no-referrer"
              />
            </a>

            <nav className="desktop-nav" aria-label="Main navigation">
              <a
                id="nav-link-home"
                href="./"
                aria-current={activeTab === 'home' ? 'page' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('home');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Home
              </a>
              <a
                id="nav-link-upload"
                href="/studio"
                aria-current={activeTab === 'shop' ? 'page' : undefined}
                onMouseEnter={preloadMagnetDesigner}
                onTouchStart={preloadMagnetDesigner}
                onFocus={preloadMagnetDesigner}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('shop');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Upload &amp; Customize
              </a>
              <a
                id="nav-link-browse"
                href="/browse"
                aria-current={activeTab === 'browse' ? 'page' : undefined}
                onMouseEnter={preloadPremade}
                onTouchStart={preloadPremade}
                onFocus={preloadPremade}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('browse');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'instant' });
                }}
              >
                Browse Designs
              </a>
              <a
                id="nav-link-moments"
                href="/moments"
                aria-current={activeTab === 'moments' ? 'page' : undefined}
                onMouseEnter={preloadMomentsCreator}
                onTouchStart={preloadMomentsCreator}
                onFocus={preloadMomentsCreator}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('moments');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Moments Creator
              </a>
              <a
                id="nav-link-fundraising"
                href="/fundraising"
                aria-current={activeTab === 'fundraising' ? 'page' : undefined}
                onMouseEnter={preloadFundraising}
                onTouchStart={preloadFundraising}
                onFocus={preloadFundraising}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('fundraising');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Fundraising
              </a>
              <a
                id="nav-link-giftcards"
                href="/gift-cards"
                aria-current={activeTab === 'giftcards' ? 'page' : undefined}
                onMouseEnter={preloadGiftCards}
                onTouchStart={preloadGiftCards}
                onFocus={preloadGiftCards}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('giftcards');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Gift Cards
              </a>
              {savedDesigns.length > 0 && (
                <a
                  id="nav-link-saved-designs"
                  href="/?page=saved_designs"
                  aria-current={activeTab === 'dashboard' && dashboardTab === 'saved_designs' ? 'page' : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('dashboard');
                    setDashboardTab('saved_designs');
                    setSelectedOrderTracking(null);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  My Designs ({savedDesigns.length})
                </a>
              )}
            </nav>

            <div className="header-actions">
              {auth.token ? (
                <>
                  <button
                    id="user-account-btn"
                    type="button"
                    className="icon-button header-account relative"
                    onClick={() => {
                      setActiveTab('dashboard');
                      setSelectedOrderTracking(null);
                      setIsMobileMenuOpen(false);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    aria-label="My SquareMagnets account"
                    title={auth.user?.name ? `${auth.user.name} (My Account)` : 'My Account'}
                  >
                    <svg className="icon"><use href="#user" /></svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="icon-button header-account"
                    aria-label="Sign Out"
                    title="Sign Out"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <a
                  id="sign-in-btn"
                  className="icon-button header-account"
                  href="/?page=saved_designs"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowAuthModal(true);
                  }}
                  aria-label="Sign in to your SquareMagnets account"
                  title="Sign in / My account"
                >
                  <svg className="icon"><use href="#user" /></svg>
                </a>
              )}

              <a
                id="header-cart-button"
                className="icon-button relative"
                href="/cart"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('cart');
                  setSelectedOrderTracking(null);
                  setIsMobileMenuOpen(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                aria-label="Shopping cart"
                title="Shopping cart"
              >
                <svg className="icon"><use href="#bag" /></svg>
                {cartItemCount > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '6px',
                      right: '1px',
                      background: 'var(--navy)',
                      color: '#fff',
                      fontSize: '9px',
                      fontWeight: 700,
                      minWidth: '16px',
                      height: '16px',
                      borderRadius: '9999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px',
                      lineHeight: 1
                    }}
                  >
                    {cartItemCount}
                  </span>
                )}
              </a>

              <button
                id="mobile-hamburger-btn"
                type="button"
                className="icon-button menu-toggle"
                aria-label={isMobileMenuOpen ? 'Close navigation' : 'Open navigation'}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-nav"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <svg className="icon"><use href="#menu" /></svg>
              </button>
            </div>
          </div>

          <nav id="mobile-nav" className={`mobile-nav ${isMobileMenuOpen ? 'is-open' : ''}`} aria-label="Mobile navigation">
            <a
              href="./"
              aria-current={activeTab === 'home' ? 'page' : undefined}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('home');
                setSelectedOrderTracking(null);
                setIsMobileMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Home
            </a>
            <a
              href="/studio"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('shop');
                setSelectedOrderTracking(null);
                setIsMobileMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Upload &amp; Customize
            </a>
            <a
              href="/browse"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('browse');
                setSelectedOrderTracking(null);
                setIsMobileMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'instant' });
              }}
            >
              Browse Designs
            </a>
            <a
              href="/moments"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('moments');
                setSelectedOrderTracking(null);
                setIsMobileMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Moments Creator
            </a>
            <a
              href="/fundraising"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('fundraising');
                setSelectedOrderTracking(null);
                setIsMobileMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Fundraising
            </a>
            <a
              href="/gift-cards"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('giftcards');
                setSelectedOrderTracking(null);
                setIsMobileMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Digital Gift Cards
            </a>
            <a
              href="/track"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('dashboard');
                setDashboardTab('orders');
                setSelectedOrderTracking(null);
                setIsMobileMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Track Order
            </a>
            <a
              href="/news"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('news');
                setSelectedNewsSlug(null);
                setSelectedOrderTracking(null);
                setIsMobileMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Blog
            </a>
            <a
              href="/?page=saved_designs"
              onClick={(e) => {
                e.preventDefault();
                setIsMobileMenuOpen(false);
                if (auth.token) {
                  setActiveTab('dashboard');
                  setDashboardTab('saved_designs');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  setShowAuthModal(true);
                }
              }}
            >
              {auth.token ? `My Account (${auth.user?.name || 'Orders'})` : 'Sign in / My account'}
            </a>
          </nav>
        </div>
      </header>

      {/* MAIN BODY VIEWPORT */}
      <main id="main" className={activeTab === 'home' ? 'flex-1 w-full sm-fresh-design text-left' : 'flex-1 max-w-7xl w-full mx-auto px-4 md:px-8 py-8 text-left sm-fresh-design'}>

        {activeTab === 'home' && (() => {
          const galleryItems = {
            photos: {
              image: '/memory-magnets.webp',
              alt: 'Real SquareMagnets family and milestone photo magnets',
              kicker: 'For the camera-roll favourites',
              titleLines: ['Too good to', 'stay on your phone.'],
              description: 'That one photo that always makes you smile? Give it a permanent place in your day. Upload your favourites and make a collection that feels like you.',
              action: 'Turn your photos into magnets',
              targetTab: 'shop' as const,
              note: 'One for you. A few for the people in the pictures.'
            },
            business: {
              image: '/square-business-magnets.webp',
              alt: 'Actual business logo and QR code magnets',
              kicker: 'For the brand you’re building',
              titleLines: ['Your business.', 'On their fridge.'],
              description: 'Give customers a reason to keep your name close. Turn your logo, artwork or QR code into a useful little reminder of your business.',
              action: 'Create business magnets',
              targetTab: 'shop' as const,
              note: 'Quantity pricing makes bigger orders even better.'
            },
            moments: {
              image: '/events-magnets.webp',
              alt: 'Actual SquareMagnets keepsakes for weddings and special events',
              kicker: 'For the once-in-a-lifetime days',
              titleLines: ['The big day.', 'In a little square.'],
              description: 'Wedding favours, save-the-dates and milestone keepsakes. Start with Moments Creator and turn your favourite occasion into something people can take home.',
              action: 'Explore Moments Creator',
              targetTab: 'moments' as const,
              note: 'For the people who were there. And the memories that stay.'
            },
            personality: {
              image: '/funny-square-magnets.webp',
              alt: 'Actual colourful quote and kitchen humour magnets',
              kicker: 'For the fridge with character',
              titleLines: ['A little humour.', 'A lot of you.'],
              description: 'Good quotes, kitchen humour and designs with personality. Browse the collection and find the one that makes you think, “That’s so me.”',
              action: 'Find your ready-made favourite',
              targetTab: 'browse' as const,
              note: 'No photo needed. Just a little personality.'
            }
          };

          const currentGallery = galleryItems[activeGalleryTab];
          const unitPriceForCalc = getUnitPriceForQuantity(calcQuantity);
          const totalForCalc = unitPriceForCalc * calcQuantity;
          const savedForCalc = Math.max(0, (pricing.pricePerMagnet - unitPriceForCalc) * calcQuantity);
          const formatCAD = (amount: number) =>
            new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);

          return (
            <>
              {/* HERO SECTION */}
              <section id="hero-section" className="hero" aria-labelledby="hero-heading">
                <div className="hero-copy">
                  <div className="eyebrow">
                    <span className="tiny-squares" aria-hidden="true"></span>
                    Custom photo magnets. Made in Canada.
                  </div>
                  <h1 id="hero-heading">
                    Your life.<br />
                    In little<br />
                    <span>squares.</span>
                    <svg className="hero-spark" viewBox="0 0 80 80" aria-hidden="true">
                      <path d="M40 0 47 29 73 12 55 36 80 40 53 47 68 72 44 55 40 80 33 53 8 69 24 44 0 40 28 33 12 8 36 25Z" fill="currentColor" />
                    </svg>
                  </h1>
                  <p>
                    The good days. The favourite faces. The dog, obviously. Make them into photo magnets you’ll love seeing every day.
                  </p>
                  <a
                    id="hero-customize-btn"
                    className="btn btn-dark"
                    href="/studio"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab('shop');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    Make your magnets
                    <svg className="icon"><use href="#arrow" /></svg>
                  </a>
                  <div className="hero-small">
                    <span>From <strong>${pricing.pricePerMagnet.toFixed(2)} CAD</strong></span>
                    <span>No minimum. All yours.</span>
                  </div>
                  <a
                    id="hero-browse-btn"
                    className="hero-secondary"
                    href="/browse"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab('browse');
                      window.scrollTo({ top: 0, behavior: 'instant' });
                    }}
                  >
                    Or find a ready-made favourite <span>↗</span>
                  </a>
                </div>

                <div className="hero-photo">
                  <img
                    src="/assets/aistudio/23afd9e2-bd32-4286-8c90-67a1957e19bd_46902d93.png"
                    alt="A hand placing actual SquareMagnets 2.5-inch square photo, event, business and quote magnets on a fridge"
                    width="1122"
                    height="1402"
                    fetchPriority="high"
                  />
                  <div className="hero-photo-label">
                    <svg className="icon"><use href="#heart" /></svg>
                    <span>
                      Less scrolling.<br />
                      <strong>More smiling.</strong>
                    </span>
                  </div>
                  <div className="hero-stamp">
                    <span>SMALL SQUARES.</span>
                    <strong>BIG<br />FEELINGS.</strong>
                    <span>2.5″ × 2.5″</span>
                  </div>
                  <span className="image-note">A little fridge inspiration.</span>
                </div>
              </section>

              {/* JOY RIBBON */}
              <div className="joy-ribbon" aria-label="Made for life's favourite moments">
                <span>GOOD DOGS.</span>
                <span className="ribbon-star" aria-hidden="true">✳</span>
                <span>BIG DAYS.</span>
                <span className="ribbon-star" aria-hidden="true">✳</span>
                <span>FAVOURITE PEOPLE.</span>
                <span className="ribbon-star" aria-hidden="true">✳</span>
                <span>EVERYDAY JOY.</span>
              </div>

              {/* GALLERY SECTION */}
              <section className="section gallery-section" id="possibilities">
                <div className="wrap">
                  <div className="section-heading">
                    <div>
                      <div className="eyebrow">Whatever matters to you.</div>
                      <h2>
                        Make room<br />
                        for the good stuff.
                      </h2>
                    </div>
                    <p>
                      Memories worth keeping. A business worth remembering. A little something that’s very, very you.
                    </p>
                  </div>

                  <div className="gallery-tabs" role="tablist" aria-label="Explore magnet ideas">
                    <button
                      type="button"
                      role="tab"
                      id="tab-photos"
                      aria-selected={activeGalleryTab === 'photos'}
                      aria-controls="gallery-panel"
                      onClick={() => setActiveGalleryTab('photos')}
                    >
                      Your photos <span>01</span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      id="tab-business"
                      aria-selected={activeGalleryTab === 'business'}
                      aria-controls="gallery-panel"
                      onClick={() => setActiveGalleryTab('business')}
                    >
                      Your business <span>02</span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      id="tab-moments"
                      aria-selected={activeGalleryTab === 'moments'}
                      aria-controls="gallery-panel"
                      onClick={() => setActiveGalleryTab('moments')}
                    >
                      Your big moments <span>03</span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      id="tab-personality"
                      aria-selected={activeGalleryTab === 'personality'}
                      aria-controls="gallery-panel"
                      onClick={() => setActiveGalleryTab('personality')}
                    >
                      Your personality <span>04</span>
                    </button>
                  </div>

                  <div className="gallery-panel" id="gallery-panel" role="tabpanel" aria-labelledby={`tab-${activeGalleryTab}`}>
                    <div className="gallery-image">
                      <img
                        id="gallery-image"
                        src={currentGallery.image}
                        alt={currentGallery.alt}
                        width="446"
                        height="277"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                      <span className="real-label">A few of our actual magnets.</span>
                    </div>
                    <div className="gallery-copy-stack">
                      {(Object.keys(galleryItems) as Array<keyof typeof galleryItems>).map((key) => {
                        const item = galleryItems[key];
                        const isActive = key === activeGalleryTab;
                        return (
                          <div
                            key={key}
                            className={`gallery-copy ${isActive ? 'is-active' : 'is-inactive'}`}
                            aria-hidden={!isActive}
                          >
                            <span id={isActive ? 'gallery-kicker' : undefined} className="eyebrow">{item.kicker}</span>
                            <h3 id={isActive ? 'gallery-title' : undefined}>
                              {item.titleLines[0]}<br />
                              {item.titleLines[1]}
                            </h3>
                            <p id={isActive ? 'gallery-description' : undefined}>{item.description}</p>
                            <a
                              id={isActive ? 'gallery-link' : undefined}
                              className="text-link"
                              tabIndex={isActive ? 0 : -1}
                              href={`/${item.targetTab === 'shop' ? 'studio' : item.targetTab}`}
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveTab(item.targetTab);
                                window.scrollTo({ top: 0, behavior: 'instant' });
                              }}
                            >
                              <span id={isActive ? 'gallery-action' : undefined}>{item.action}</span>
                              <svg className="icon"><use href="#arrow" /></svg>
                            </a>
                            <span id={isActive ? 'gallery-footnote' : undefined} className="gallery-footnote">{item.note}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </section>

              {/* CRAFT SECTION */}
              <section className="craft-section">
                <div className="wrap craft-grid">
                  <div className="craft-copy">
                    <div className="eyebrow">Crafted for everyday happiness.</div>
                    <h2>
                      A proper little<br />
                      keepsake.
                    </h2>
                    <p>
                      Pick it up and feel the difference. Each magnet is hand-pressed in Kamloops with a steel core, vivid photo printing and a glossy finish.
                    </p>
                    <div className="craft-detail">
                      <span>01</span>
                      <div>
                        <h3>Beautiful on the outside.</h3>
                        <p>Rich photo colour. Crisp detail. A glossy, wipe-clean Mylar finish.</p>
                      </div>
                    </div>
                    <div className="craft-detail">
                      <span>02</span>
                      <div>
                        <h3>Built properly on the inside.</h3>
                        <p>A solid steel core and full magnetic backing. Substantial in your hand, secure on your fridge.</p>
                      </div>
                    </div>
                    <div className="craft-detail">
                      <span>03</span>
                      <div>
                        <h3>Made right here.</h3>
                        <p>Handcrafted and inspected in Kamloops, British Columbia. Shipped across Canada.</p>
                      </div>
                    </div>
                  </div>
                  <div className="craft-photo">
                    <img
                      src="/handcrafted-magnets.webp"
                      alt="The printed front and full magnetic back of an actual SquareMagnets magnet"
                      width="600"
                      height="441"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                    />
                    <div className="craft-badge">
                      <svg className="icon"><use href="#leaf" /></svg>
                      <span>
                        Made with care.<br />
                        <strong>In Canada.</strong>
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* PROCESS SECTION */}
              <section className="process-section" id="how-it-works">
                <div className="wrap">
                  <div className="process-intro">
                    <div className="eyebrow">Your part is the fun part.</div>
                    <h2>Phone. Fridge. Happy.</h2>
                  </div>
                  <div className="process-grid">
                    <div className="step">
                      <span className="step-number">01</span>
                      <h3>Pick your favourites.</h3>
                      <p>Upload from your phone or computer, or choose a ready-made design.</p>
                    </div>
                    <div className="step">
                      <span className="step-number">02</span>
                      <h3>Make them yours.</h3>
                      <p>Crop, add text and check your preview. It’s all right there in the studio.</p>
                    </div>
                    <div className="step">
                      <span className="step-number">03</span>
                      <h3>We’ll take it from here.</h3>
                      <p>Handcrafted in Kamloops and dispatched in 1–7 business days.</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* CUSTOMER PHOTO GALLERY SECTION (ELFSIGHT) */}
              <Suspense fallback={<div className="py-12 text-center text-slate-400 font-medium">Loading Customer Gallery...</div>}>
                <ElfsightGallery />
              </Suspense>

              {/* PRICING SECTION */}
              <section className="section pricing-section" id="pricing">
                <div className="wrap pricing-grid">
                  <div className="pricing-copy">
                    <div className="eyebrow">More to love. Less per magnet.</div>
                    <h2>
                      Good things<br />
                      come in<br />
                      <span>multiples.</span>
                    </h2>
                    <p>
                      One favourite is a lovely start. Build a collection to share, with a lower price per magnet as your order grows.
                    </p>
                    <div className="price-facts">
                      <div>
                        <svg className="icon"><use href="#check" /></svg>
                        No minimum order
                      </div>
                      <div>
                        <svg className="icon"><use href="#check" /></svg>
                        Free tracked Canada shipping at ${(pricing.freeShippingThreshold ?? 75) % 1 === 0 ? (pricing.freeShippingThreshold ?? 75).toFixed(0) : (pricing.freeShippingThreshold ?? 75).toFixed(2)}+
                      </div>
                      <div>
                        <svg className="icon"><use href="#check" /></svg>
                        ${(pricing.kamloopsShippingRate ?? 5) % 1 === 0 ? (pricing.kamloopsShippingRate ?? 5).toFixed(0) : (pricing.kamloopsShippingRate ?? 5).toFixed(2)} local delivery in {pricing.localDeliveryCityName || 'Kamloops'}
                      </div>
                    </div>
                    <span className="pricing-doodle" aria-hidden="true">↗</span>
                  </div>

                  <div className="price-card">
                    <div className="price-top">
                      <h3>A little happiness, priced simply.</h3>
                      <span>CAD</span>
                    </div>

                    <div className="quantity-row">
                      <label htmlFor="quantity">How many magnets?</label>
                      <div className="quantity-control">
                        <button
                          type="button"
                          id="decrease"
                          aria-label="Decrease magnet quantity"
                          disabled={calcQuantity <= 1}
                          onClick={() => setCalcQuantity(Math.max(1, calcQuantity - 1))}
                        >
                          −
                        </button>
                        <input
                          id="quantity"
                          type="number"
                          min="1"
                          max="10000"
                          step="1"
                          value={calcQuantity}
                          inputMode="numeric"
                          aria-describedby="estimate-note"
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            if (Number.isFinite(val)) {
                              setCalcQuantity(Math.min(10000, Math.max(1, val)));
                            }
                          }}
                        />
                        <button
                          type="button"
                          id="increase"
                          aria-label="Increase magnet quantity"
                          disabled={calcQuantity >= 10000}
                          onClick={() => setCalcQuantity(Math.min(10000, calcQuantity + 1))}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="price-presets" aria-label="Choose an example quantity">
                      <button
                        className={`preset ${calcQuantity === 12 ? 'active' : ''}`}
                        type="button"
                        aria-pressed={calcQuantity === 12}
                        onClick={() => setCalcQuantity(12)}
                      >
                        12 magnets
                        <strong>${getUnitPriceForQuantity(12).toFixed(2)}</strong>
                        <small>each · a lovely gift</small>
                      </button>
                      <button
                        className={`preset ${calcQuantity === 24 ? 'active' : ''}`}
                        type="button"
                        aria-pressed={calcQuantity === 24}
                        onClick={() => setCalcQuantity(24)}
                      >
                        24 magnets
                        <strong>${getUnitPriceForQuantity(24).toFixed(2)}</strong>
                        <small>each · more to share</small>
                      </button>
                      <button
                        className={`preset ${calcQuantity === 48 ? 'active' : ''}`}
                        type="button"
                        aria-pressed={calcQuantity === 48}
                        onClick={() => setCalcQuantity(48)}
                      >
                        48 magnets
                        <strong>${getUnitPriceForQuantity(48).toFixed(2)}</strong>
                        <small>each · bigger savings</small>
                      </button>
                    </div>

                    <div className="estimate" aria-live="polite" aria-atomic="true">
                      <div>
                        <p id="estimate-label">
                          Subtotal for {calcQuantity.toLocaleString('en-CA')} {calcQuantity === 1 ? 'magnet' : 'magnets'}
                        </p>
                        <strong id="total">{formatCAD(totalForCalc)}</strong>
                      </div>
                      <div className="estimate-unit">
                        <strong id="unit-price">{formatCAD(unitPriceForCalc)}</strong>
                        per magnet
                      </div>
                    </div>

                    <div className="savings" aria-live="polite">
                      <svg className="icon"><use href="#check" /></svg>
                      <span id="saving-message">
                        {savedForCalc > 0.005
                          ? `You save ${formatCAD(savedForCalc)} with quantity pricing.`
                          : 'Start with one favourite. Save more when you add more.'}
                      </span>
                    </div>

                    <a
                      className="btn btn-primary"
                      href="/studio"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveTab('shop');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Start creating your magnets
                      <svg className="icon"><use href="#arrow" /></svg>
                    </a>

                    <p id="estimate-note" className="price-note">
                      Estimate before tax, shipping and optional add-ons.<br />
                      Choose your final designs and quantities in the studio.
                    </p>

                    <details className="all-tiers">
                      <summary>See all quantity prices</summary>
                      <table>
                        <caption className="sr-only">Magnet prices by order quantity, in Canadian dollars</caption>
                        <thead>
                          <tr>
                            <th scope="col">Quantity</th>
                            <th scope="col">Price per magnet</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td>1</td><td>${pricing.pricePerMagnet.toFixed(2)}</td></tr>
                          <tr><td>2–5</td><td>${getUnitPriceForQuantity(2).toFixed(2)}</td></tr>
                          <tr><td>6–8</td><td>${getUnitPriceForQuantity(6).toFixed(2)}</td></tr>
                          <tr><td>9–11</td><td>${getUnitPriceForQuantity(9).toFixed(2)}</td></tr>
                          <tr><td>12–17</td><td>${getUnitPriceForQuantity(12).toFixed(2)}</td></tr>
                          <tr><td>18–23</td><td>${getUnitPriceForQuantity(18).toFixed(2)}</td></tr>
                          <tr><td>24–47</td><td>${getUnitPriceForQuantity(24).toFixed(2)}</td></tr>
                          <tr><td>48–249</td><td>${getUnitPriceForQuantity(48).toFixed(2)}</td></tr>
                          <tr><td>250+</td><td>${getUnitPriceForQuantity(250).toFixed(2)}</td></tr>
                        </tbody>
                      </table>
                    </details>
                  </div>
                </div>
              </section>

              {/* FUNDRAISING SECTION */}
              <section className="section fundraise-section">
                <div className="wrap fundraise-split">
                  <div className="fundraise-intro">
                    <div className="eyebrow">Schools. Teams. Communities.</div>
                    <h2>
                      Make memories.<br />
                      Make a difference.
                    </h2>
                    <p>
                      A fundraiser families can make their own, with no upfront costs, no door-to-door order forms, and keepsake magnets supporters actually want on their fridge.
                    </p>
                    <a
                      className="btn btn-primary"
                      href="/fundraising"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveTab('fundraising');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Let’s raise something good
                      <svg className="icon"><use href="#arrow" /></svg>
                    </a>
                  </div>

                  <article className="fundraise-card">
                    <div className="fundraise-number">
                      <span>Earn up to</span>
                      <strong>30<span>%</span></strong>
                      <span>for your group.</span>
                    </div>
                    <div className="fundraise-copy">
                      <h3>
                        Good for your fridge.<br />
                        Great for your cause.
                      </h3>
                      <ul className="fundraise-perks">
                        <li><svg className="icon"><use href="#check" /></svg> Zero upfront costs or minimums</li>
                        <li><svg className="icon"><use href="#check" /></svg> Dedicated shareable campaign link</li>
                        <li><svg className="icon"><use href="#check" /></svg> Handcrafted &amp; shipped across Canada</li>
                      </ul>
                      <a
                        className="text-link"
                        href="/fundraising"
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab('fundraising');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        See how fundraising works
                        <svg className="icon"><use href="#arrow" /></svg>
                      </a>
                    </div>
                  </article>
                </div>
              </section>

              {/* NFC MAGNETS SECTION */}
              <section className="section nfc-section">
                <div className="wrap nfc-split">
                  <div className="nfc-intro">
                    <div className="nfc-top">
                      <svg className="icon"><use href="#nfc" /></svg>
                      <span>MEET NFC MAGNETS</span>
                    </div>
                    <h2>
                      A little square.<br />
                      Another dimension.
                    </h2>
                    <p>
                      A favourite photo that opens a video. A business magnet that opens your website. Add an embedded NFC tag and give your magnet something more to say with a simple tap.
                    </p>
                  </div>

                  <article className="nfc-card">
                    <div className="nfc-card-header">
                      <div>
                        <span className="eyebrow">Tap-to-open technology</span>
                        <h3>Built right inside the magnet.</h3>
                      </div>
                      <span className="nfc-price-badge">+$1.50 / magnet</span>
                    </div>
                    <p>
                      Link to a shared photo album, wedding video, Spotify playlist, booking page, or digital business card. Works with any modern smartphone — no app required.
                    </p>
                    <div className="nfc-footer">
                      <a
                        className="text-link"
                        href="/studio"
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab('shop');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        Make yours smarter in the Studio
                        <svg className="icon"><use href="#arrow" /></svg>
                      </a>
                      <small>Optional add-on at checkout. Compatible phone required.</small>
                    </div>
                  </article>
                </div>
              </section>

              {/* FAQ SECTION */}
              <section className="section faq-section" id="questions">
                <div className="wrap faq-grid">
                  <div className="faq-copy">
                    <div className="eyebrow">Before you make your magnets</div>
                    <h2>
                      Good questions.<br />
                      Simple answers.
                    </h2>
                    <p>A few details before your favourite photos become your favourite little things.</p>
                    <a
                      className="text-link"
                      href="/news"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveTab('news');
                        setSelectedNewsSlug(null);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      More ideas &amp; design guides
                      <svg className="icon"><use href="#arrow" /></svg>
                    </a>
                  </div>

                  <div className="faq-list">
                    {[
                      {
                        q: 'What size are the magnets?',
                        a: 'Every square magnet measures 2.5″ × 2.5″ (63.5 mm × 63.5 mm), with a rigid steel core, a full magnetic back and a protective glossy Mylar finish.'
                      },
                      {
                        q: 'Can I use photos from my phone?',
                        a: 'Yes. Upload your photos from your phone or computer in the design studio. You can crop them, add text and check the preview before you order. For the clearest result, start with your original, high-resolution photos.'
                      },
                      {
                        q: 'How much does shipping cost?',
                        a: 'Tracked shipping within Canada is free on orders of $75 or more, and $10 for smaller orders. Local delivery in Kamloops is $5. Your final shipping charge appears at checkout.'
                      },
                      {
                        q: 'When will my magnets be shipped?',
                        a: 'Magnets are handcrafted in Kamloops and dispatched within 1–7 business days. Delivery time depends on your destination. Use Track Order to follow production and shipping updates.'
                      },
                      {
                        q: 'Is there a minimum order?',
                        a: 'No. Order just one magnet or build a larger collection. Quantity pricing starts at $5.99 for one and reaches $2.49 per magnet for orders of 250 or more. Discounts apply automatically.'
                      },
                      {
                        q: 'Can I make magnets for a business or fundraiser?',
                        a: 'Absolutely. Upload your logo or artwork in the studio for business magnets. For schools, teams and community groups, visit Fundraising for campaign details and eligibility. You can also add an optional programmed NFC tag at checkout.'
                      }
                    ].map((faq, idx) => (
                      <details key={idx} open={openHomeFaq === idx}>
                        <summary
                          onClick={(e) => {
                            e.preventDefault();
                            setOpenHomeFaq(openHomeFaq === idx ? null : idx);
                          }}
                        >
                          {faq.q}
                        </summary>
                        <p>{faq.a}</p>
                      </details>
                    ))}
                  </div>
                </div>
              </section>

              {/* CLOSING CTA SECTION */}
              <section className="closing">
                <div className="wrap">
                  <div className="eyebrow">A small gift. A very good feeling.</div>
                  <h2>
                    Make their fridge.<br />
                    <span>Make their day.</span>
                  </h2>
                  <a
                    className="btn btn-dark"
                    href="/studio"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab('shop');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    Let’s make your magnets
                    <svg className="icon"><use href="#arrow" /></svg>
                  </a>
                  <span className="closing-note">For someone you love. Yourself included.</span>
                </div>
                <span className="closing-square" aria-hidden="true"></span>
              </section>
            </>
          );
        })()}

        {activeTab === 'browse' && (
          <div className="space-y-6 animate-fadeIn">
            <Suspense fallback={<div className="py-12 text-center text-slate-400 font-medium">Loading Premade Designs...</div>}>
              <PremadeBrowser
                onAddToCart={handleAddDesignToCart}
                onCustomize={handleCustomizePremade}
                onViewCart={() => setActiveTab('cart')}
                autoOpenPremadeId={initialPremadeId}
                onClearAutoOpen={() => setInitialPremadeId(null)}
              />
            </Suspense>
          </div>
        )}

        {activeTab === 'moments' && (
          <div className="space-y-6 animate-fadeIn">
            <Suspense fallback={<div className="py-12 text-center text-slate-400 font-medium">Loading Moments Creator...</div>}>
              <MomentsCreator
                onAddDesignToCart={handleAddDesignToCart}
                pricingConfig={pricing}
                pricingLoaded={pricingLoaded}
                cart={cart}
                editingDesignId={editingDesignId}
                onCancelEdit={() => setEditingDesignId(null)}
                onSwitchToShop={() => setActiveTab('shop')}
              />
            </Suspense>
          </div>
        )}

        {activeTab === 'shop' && (
          <div className="space-y-6 animate-fadeIn">
            <Suspense fallback={<div className="py-12 text-center text-slate-400 font-medium">Loading Magnet Studio...</div>}>
              <MagnetDesigner
                onAddDesignToCart={handleAddDesignToCart}
                onAddMultipleDesignsToCart={handleAddMultipleDesignsToCart}
                pricingConfig={pricing}
                pricingLoaded={pricingLoaded}
                cart={cart}
                initialSelectedImageId={editingDesignId}
                initialCustomDesign={customDesignToLoad}
                onClearInitialCustomDesign={() => setCustomDesignToLoad(null)}
                onSaveDesign={handleSaveDesign}
                isLoggedIn={!!auth.token}
                savedDesigns={savedDesigns}
                onRemoveFromCart={handleRemoveFromCart}
              />
            </Suspense>
          </div>
        )}

        {activeTab === 'cart' && (
          <Suspense fallback={
            <div className="flex items-center justify-center p-12 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          }>
            <CheckoutWizard
              cart={cart}
              pricing={pricing}
              cartItemCount={cartItemCount}
              cartSubtotal={cartSubtotal}
              shippingCost={shippingCost}
              cartTotal={cartTotal}
              unitPrice={unitPrice}
              finalDiscount={finalDiscount}
              appliedCoupon={appliedCoupon}
              couponCodeInput={couponCodeInput}
              setCouponCodeInput={setCouponCodeInput}
              handleApplyCoupon={handleApplyCoupon}
              handleRemoveCoupon={handleRemoveCoupon}
              couponLoading={couponLoading}
              couponError={couponError}
              getCouponUsageExplanation={getCouponUsageExplanation}
              handleEditCartItem={handleEditCartItem}
              setPreviewItem={setPreviewItem}
              getSavedDesignForMagnet={getSavedDesignForMagnet}
              handleSaveDesign={handleSaveDesign}
              handleRemoveFromCart={handleRemoveFromCart}
              handleUpdateCartItemQuantity={handleUpdateCartItemQuantity}
              handleUpdateCartItemNfc={handleUpdateCartItemNfc}
              setActiveTab={setActiveTab}
              shippingName={shippingName}
              setShippingName={setShippingName}
              guestEmail={guestEmail}
              setGuestEmail={setGuestEmail}
              customerPhone={customerPhone}
              setCustomerPhone={setCustomerPhone}
              shippingAddress={shippingAddress}
              setShippingAddress={setShippingAddress}
              shippingCity={shippingCity}
              setShippingCity={setShippingCity}
              shippingZip={shippingZip}
              setShippingZip={setShippingZip}
              shippingCountry={shippingCountry}
              setShippingCountry={setShippingCountry}
              ALLOW_ONLY_CANADA_SHIPPING={ALLOW_ONLY_CANADA_SHIPPING}
              billingSameAsShipping={billingSameAsShipping}
              setBillingSameAsShipping={setBillingSameAsShipping}
              billingName={billingName}
              setBillingName={setBillingName}
              billingAddress={billingAddress}
              setBillingAddress={setBillingAddress}
              billingCity={billingCity}
              setBillingCity={setBillingCity}
              billingZip={billingZip}
              setBillingZip={setBillingZip}
              billingCountry={billingCountry}
              setBillingCountry={setBillingCountry}
              isGift={isGift}
              setIsGift={setIsGift}
              giftMessage={giftMessage}
              setGiftMessage={setGiftMessage}
              giftTo={giftTo}
              setGiftTo={setGiftTo}
              giftFrom={giftFrom}
              setGiftFrom={setGiftFrom}
              specialInstructions={specialInstructions}
              setSpecialInstructions={setSpecialInstructions}
              allowSocialMediaFeatured={allowSocialMediaFeatured}
              setAllowSocialMediaFeatured={setAllowSocialMediaFeatured}
              fundraiserStudentName={fundraiserStudentName}
              setFundraiserStudentName={setFundraiserStudentName}
              fundraiserClassroom={fundraiserClassroom}
              setFundraiserClassroom={setFundraiserClassroom}
              fundraiserDeliveryMethod={fundraiserDeliveryMethod}
              setFundraiserDeliveryMethod={setFundraiserDeliveryMethod}
              addressSuggestions={addressSuggestions}
              setAddressSuggestions={setAddressSuggestions}
              showSuggestions={showSuggestions}
              setShowSuggestions={setShowSuggestions}
              isFetchingSuggestions={isFetchingSuggestions}
              setLastSelectedStreet={setLastSelectedStreet}
              billingAddressSuggestions={billingAddressSuggestions}
              setBillingAddressSuggestions={setBillingAddressSuggestions}
              showBillingSuggestions={showBillingSuggestions}
              setShowBillingSuggestions={setShowBillingSuggestions}
              isFetchingBillingSuggestions={isFetchingBillingSuggestions}
              setLastSelectedBillingStreet={setLastSelectedBillingStreet}
              isLocalKamloopsAddress={isLocalKamloopsAddress}
              setIsLocalKamloopsAddress={setIsLocalKamloopsAddress}
              checkLocalShippingDiscount={checkLocalShippingDiscount}
              geocodeStatus={geocodeStatus}
              isGeocoding={isGeocoding}
              isLocalPromoApplied={isLocalPromoApplied}
              checkoutError={checkoutError}
              setCheckoutError={setCheckoutError}
              isProcessingPayment={isProcessingPayment}
              handleCheckoutSubmit={handleCheckoutSubmit}
              handleExpressCheckout={handleExpressCheckout}
              checkoutStep={checkoutStep}
              setCheckoutStep={setCheckoutStep}
            />
          </Suspense>
        )}

        {activeTab === 'dashboard' && (
          <div className="w-full space-y-6 animate-fadeIn" id="customer-orders-tab">

            <div className="sm-page-banner flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div>
                <div className="eyebrow">
                  <span className="tiny-squares" aria-hidden="true"></span>
                  {auth.token && dashboardTab === 'saved_designs' ? 'Your personal template collection.' : 'Handcrafted in Kamloops. Tracked across Canada.'}
                </div>
                <h1>
                  {auth.token && dashboardTab === 'saved_designs' ? (
                    <>Saved magnet <span>designs.</span></>
                  ) : (
                    <>Track your <span>magnets.</span></>
                  )}
                </h1>
                <p className="mt-3 max-w-xl">
                  {auth.token && dashboardTab === 'saved_designs' 
                    ? 'Manage your saved custom templates and re-order your favourite little squares with a single click.' 
                    : 'Follow your custom magnet order from our Kamloops studio to your front door in real time.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab('shop');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="btn btn-dark !min-h-[48px] !py-3 !px-5 !text-xs"
                >
                  <span>Make new magnets</span>
                  <svg className="icon"><use href="#arrow" /></svg>
                </button>
              </div>
            </div>

            {auth.token && !isFetchingOrderAfterCheckout && (
              <div className="flex gap-2 border-b border-[#c8d5dc] pb-px">
                <button
                  onClick={() => setDashboardTab('orders')}
                  className={`pb-3.5 pt-2 px-5 text-xs font-semibold transition relative cursor-pointer flex items-center gap-2 ${
                    dashboardTab === 'orders' ? 'text-[#062d4c] border-b-[3px] border-[#006cbb] font-bold' : 'text-[#566a78] hover:text-[#006cbb]'
                  }`}
                >
                  <Truck className="w-4 h-4" />
                  <span>Orders &amp; Live Tracker</span>
                  {loadingOrders && (
                    <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-[#0086e5] animate-ping" title="Syncing with database..." />
                  )}
                </button>
                <button
                  onClick={() => setDashboardTab('saved_designs')}
                  className={`pb-3.5 pt-2 px-5 text-xs font-semibold transition relative cursor-pointer flex items-center gap-2 ${
                    dashboardTab === 'saved_designs' ? 'text-[#062d4c] border-b-[3px] border-[#006cbb] font-bold' : 'text-[#566a78] hover:text-[#006cbb]'
                  }`}
                >
                  <Heart className="w-4 h-4" />
                  <span>Saved Designs ({savedDesigns.length})</span>
                  {loadingSavedDesigns && (
                    <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-[#0086e5] animate-ping" title="Syncing with master server..." />
                  )}
                </button>
              </div>
            )}

            {/* Post-Checkout Loading Skeleton (shown while fetching and syncing order details from Stripe) */}
            {isFetchingOrderAfterCheckout ? (
              <div className="space-y-6 animate-fadeIn" id="track-order-loading-skeleton">
                {/* Processing Status Banner */}
                <div className="bg-emerald-50 border border-emerald-200/90 rounded-2xl p-5 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-2xl bg-emerald-600 text-white flex items-center justify-center shadow-sm shrink-0">
                      <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-extrabold text-emerald-950">Payment Confirmed by Stripe</h3>
                        <span className="px-2.5 py-0.5 rounded-full bg-emerald-100/90 text-emerald-800 text-[10px] font-extrabold uppercase tracking-wider">
                          Syncing Order
                        </span>
                      </div>
                      <p className="text-xs text-emerald-800/90 font-medium mt-0.5 leading-relaxed">
                        Please wait a moment while we retrieve your order records and generate your real-time shipping tracking timeline...
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-mono font-bold text-emerald-800 bg-white/80 px-3.5 py-1.5 rounded-xl border border-emerald-200 shrink-0 self-end sm:self-auto shadow-xs">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                    <span>Loading Order Details...</span>
                  </div>
                </div>

                {/* 2-Column Responsive Skeleton Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start animate-pulse">
                  {/* Left Column (Order Summary Card Skeleton) */}
                  <div className="lg:col-span-5 space-y-4">
                    <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm space-y-5">
                      {/* Header row */}
                      <div className="border-b border-slate-100 pb-4 flex justify-between items-start">
                        <div className="space-y-2">
                          <div className="h-3 w-20 bg-slate-200 rounded-md" />
                          <div className="h-5 w-32 bg-slate-250 rounded-lg" />
                        </div>
                        <div className="h-6 w-20 bg-emerald-150 rounded-full" />
                      </div>

                      {/* Invoice action placeholder */}
                      <div className="h-14 bg-emerald-50/60 rounded-2xl border border-emerald-100/80 p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-xl bg-emerald-150" />
                          <div className="space-y-1.5">
                            <div className="h-3 w-28 bg-emerald-200 rounded" />
                            <div className="h-2.5 w-36 bg-emerald-150 rounded" />
                          </div>
                        </div>
                        <div className="h-8 w-24 bg-emerald-200 rounded-xl" />
                      </div>

                      {/* Details list skeleton */}
                      <div className="space-y-3 pt-1">
                        <div className="flex justify-between items-center py-1 border-b border-slate-100">
                          <div className="h-3 w-24 bg-slate-150 rounded" />
                          <div className="h-3 w-32 bg-slate-200 rounded" />
                        </div>
                        <div className="flex justify-between items-center py-1 border-b border-slate-100">
                          <div className="h-3 w-28 bg-slate-150 rounded" />
                          <div className="h-3 w-20 bg-slate-200 rounded" />
                        </div>
                        <div className="flex justify-between items-center py-1 border-b border-slate-100">
                          <div className="h-3 w-20 bg-slate-150 rounded" />
                          <div className="h-3 w-24 bg-slate-200 rounded" />
                        </div>
                        <div className="flex justify-between items-center pt-2">
                          <div className="h-4 w-24 bg-slate-200 rounded" />
                          <div className="h-5 w-24 bg-slate-300 rounded" />
                        </div>
                      </div>

                      {/* Shipping recipient skeleton */}
                      <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100 space-y-2">
                        <div className="h-3 w-28 bg-slate-200 rounded" />
                        <div className="h-3 w-40 bg-slate-150 rounded" />
                        <div className="h-3 w-32 bg-slate-150 rounded" />
                      </div>
                    </div>
                  </div>

                  {/* Right Column (Courier Shipping Tracker Card Skeleton) */}
                  <div className="lg:col-span-7 space-y-4">
                    <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm space-y-6">
                      {/* Tracker header */}
                      <div className="border-b border-slate-100 pb-4 flex justify-between items-start">
                        <div className="space-y-2">
                          <div className="h-3 w-28 bg-orange-150 rounded-md" />
                          <div className="h-5 w-44 bg-slate-250 rounded-lg" />
                        </div>
                        <div className="h-6 w-28 bg-slate-150 rounded-full" />
                      </div>

                      {/* Carrier box placeholder */}
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-150 flex items-center justify-between">
                        <div className="space-y-1.5">
                          <div className="h-2.5 w-24 bg-slate-200 rounded" />
                          <div className="h-4 w-32 bg-slate-250 rounded" />
                        </div>
                        <div className="space-y-1.5 text-right">
                          <div className="h-2.5 w-20 bg-slate-200 rounded ml-auto" />
                          <div className="h-4 w-28 bg-orange-150 rounded ml-auto" />
                        </div>
                      </div>

                      {/* Progress bar placeholder */}
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <div className="h-3 w-32 bg-slate-200 rounded" />
                          <div className="h-3 w-16 bg-slate-200 rounded" />
                        </div>
                        <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-orange-200 rounded-full w-1/4 animate-pulse" />
                        </div>
                      </div>

                      {/* 4-Step Timeline Checkpoints Skeleton */}
                      <div className="space-y-4 pt-2">
                        {[
                          { label: 'Order Received & Verified', sub: 'Payment confirmed via Stripe' },
                          { label: 'Custom Magnet Prepress', sub: 'High-resolution printing & 30-mil magnet molding' },
                          { label: 'Quality Inspection & Lamination', sub: 'High-gloss protective Mylar finish applied' },
                          { label: 'Courier Dispatch & Tracking', sub: 'Direct delivery to your destination' }
                        ].map((step, idx) => (
                          <div key={idx} className="flex gap-4 items-start">
                            <div className="w-9 h-9 rounded-full bg-slate-150 border-2 border-slate-200 flex items-center justify-center shrink-0">
                              <div className="w-3.5 h-3.5 rounded-full bg-slate-300" />
                            </div>
                            <div className="flex-grow space-y-1.5 pt-0.5">
                              <div className="h-3.5 w-44 bg-slate-250 rounded" />
                              <div className="h-2.5 w-56 bg-slate-150 rounded" />
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Items preview skeleton */}
                      <div className="border-t border-slate-100 pt-4 space-y-3">
                        <div className="h-3 w-32 bg-slate-200 rounded" />
                        <div className="grid grid-cols-3 gap-3">
                          {[1, 2, 3].map((i) => (
                            <div key={i} className="aspect-square bg-slate-100 rounded-xl border border-slate-150 flex items-center justify-center relative">
                              <div className="w-8 h-8 rounded-lg bg-slate-200" />
                              <div className="absolute top-1.5 right-1.5 w-6 h-4 bg-slate-200 rounded-md" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Post-Checkout Success Notification Banner (once loaded) */}
            {!isFetchingOrderAfterCheckout && paymentSuccessOrder && (
              <div className="bg-[#d8eeee] border border-[#317c8b]/30 rounded-[5px] p-4 sm:p-5 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 animate-fadeIn" id="checkout-success-banner">
                <div className="flex items-start sm:items-center gap-3.5">
                  <div className="w-10 h-10 rounded-[5px] bg-[#062d4c] text-white flex items-center justify-center shadow-sm shrink-0 mt-0.5 sm:mt-0">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold text-[#062d4c]">
                        Order #{paymentSuccessOrder.id} Placed Successfully!
                      </h3>
                      <span className="px-2.5 py-0.5 rounded-[3px] bg-[#062d4c] text-white text-[10px] font-semibold uppercase tracking-wider">
                        Stripe Paid
                      </span>
                    </div>
                    <p className="text-xs text-[#16494c] font-medium mt-0.5 leading-relaxed">
                      Thank you for your order! We've received your payment and our prepress team is preparing your custom magnets for production.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
                  <button
                    type="button"
                    onClick={() => generateInvoicePDF(paymentSuccessOrder)}
                    className="px-4 py-2.5 bg-[#062d4c] hover:bg-[#114e74] text-white font-semibold text-xs rounded-[5px] shadow-xs transition flex items-center gap-1.5 cursor-pointer"
                    title="Download official PDF invoice receipt"
                  >
                    <FileText className="w-3.5 h-3.5 text-[#40d0d0]" />
                    Download Invoice
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentSuccessOrder(null)}
                    className="p-2 text-[#062d4c] hover:bg-[#062d4c]/10 rounded-[5px] transition cursor-pointer"
                    title="Dismiss notice"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {!isFetchingOrderAfterCheckout && auth.token ? (
              dashboardTab === 'saved_designs' ? (
                loadingSavedDesigns && savedDesigns.length === 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 animate-pulse" id="saved-designs-skeleton">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-white border border-slate-200 rounded-[5px] overflow-hidden shadow-xs flex flex-col">
                        <div className="aspect-square bg-slate-100 relative border-b border-slate-100 flex items-center justify-center overflow-hidden">
                          <div className="w-full h-full bg-slate-100" />
                          <div className="absolute top-2 right-2 bg-slate-200 w-12 h-4 rounded-full" />
                        </div>
                        <div className="p-4 flex-grow flex flex-col justify-between space-y-3">
                          <div className="space-y-2">
                            <div className="h-4 bg-slate-200 rounded-md w-3/4" />
                            <div className="h-3 bg-slate-150 rounded-md w-1/2" />
                          </div>
                          <div className="flex flex-col gap-2 pt-2">
                            <div className="flex gap-2">
                              <div className="flex-1 h-8 bg-slate-200 rounded-[5px]" />
                              <div className="flex-1 h-8 bg-slate-150 rounded-[5px]" />
                            </div>
                            <div className="h-8 bg-slate-200 rounded-[5px] w-full" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : savedDesigns.length === 0 ? (
                  <div className="bg-[#fffefb] border border-[#d7e0e3] p-12 rounded-[5px] text-center space-y-4 shadow-xs">
                    <Heart className="w-8 h-8 text-[#006cbb] mx-auto" />
                    <h3 className="text-lg font-semibold text-[#062d4c]">No saved designs yet</h3>
                    <p className="text-xs text-[#566a78] max-w-sm mx-auto leading-relaxed">
                      Design custom magnets in our <strong>Square Magnet Studio</strong> and click "Save to My Designs" to store templates here for quick one-click re-ordering!
                    </p>
                    <button
                      onClick={() => setActiveTab('shop')}
                      className="btn btn-dark !min-h-[46px] !py-2.5 !px-6 !text-xs mx-auto"
                    >
                      <Palette className="w-4 h-4" />
                      <span>Go to Magnet Studio</span>
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                    {savedDesigns.map((sd) => (
                      <div key={sd.id} className="bg-[#fffefb] border border-[#d7e0e3] rounded-[5px] overflow-hidden shadow-xs flex flex-col group hover:shadow-md transition duration-200">
                        {/* Image Preview Container */}
                        <div className="aspect-square bg-[#eff2eb] relative border-b border-[#d7e0e3] flex items-center justify-center overflow-hidden">
                          <img 
                            src={sd.design.croppedImageUrl || sd.design.imageUrl} 
                            alt={sd.name} 
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-102"
                            loading="lazy"
                            decoding="async"
                            referrerPolicy="no-referrer"
                          />
                        </div>

                        {/* Info & Actions */}
                        <div className="p-5 flex-grow flex flex-col justify-between space-y-4">
                          <div>
                            <div className="flex items-start justify-between gap-2 min-h-[28px]">
                              {editingSavedDesignIdForInline === sd.id ? (
                                <form 
                                  onSubmit={(e) => {
                                    e.preventDefault();
                                    handleInlineRename(sd.id, sd.design, inlineRenameValue);
                                  }}
                                  className="flex items-center gap-1 w-full"
                                >
                                  <input
                                    type="text"
                                    required
                                    value={inlineRenameValue}
                                    onChange={(e) => setInlineRenameValue(e.target.value)}
                                    className="flex-grow px-2 py-1 text-xs bg-white border border-[#006cbb] rounded-[4px] text-[#082d48] focus:outline-none font-semibold"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') setEditingSavedDesignIdForInline(null);
                                    }}
                                  />
                                  <button
                                    type="submit"
                                    disabled={inlineRenamingInProgress === sd.id || !inlineRenameValue.trim() || inlineRenameValue.trim() === sd.name}
                                    className="p-1.5 bg-[#d8eeee] text-[#062d4c] hover:bg-[#9ce3d5] rounded-[4px] text-[10px] font-bold cursor-pointer disabled:opacity-50 transition leading-none flex items-center justify-center"
                                    title="Save Name"
                                  >
                                    <Check className="w-3 h-3" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setEditingSavedDesignIdForInline(null)}
                                    className="p-1.5 bg-slate-100 text-slate-500 hover:bg-slate-200 rounded-[4px] text-[10px] font-bold cursor-pointer transition leading-none flex items-center justify-center"
                                    title="Cancel"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </form>
                              ) : (
                                <>
                                  <h4 
                                    onDoubleClick={() => {
                                      setEditingSavedDesignIdForInline(sd.id);
                                      setInlineRenameValue(sd.name);
                                    }}
                                    className="text-sm font-semibold text-[#062d4c] group-hover:text-[#006cbb] transition truncate cursor-pointer select-none flex-grow" 
                                    title="Double-click to rename inline"
                                  >
                                    {sd.name}
                                  </h4>
                                  <button
                                    onClick={() => {
                                      setEditingSavedDesignIdForInline(sd.id);
                                      setInlineRenameValue(sd.name);
                                    }}
                                    className="text-[10px] font-bold text-[#566a78] hover:text-[#062d4c] transition cursor-pointer p-1"
                                    title="Rename design inline"
                                  >
                                    <Edit3 className="w-3.5 h-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                            <p className="text-[11px] text-[#566a78] mt-1 font-medium">
                              Saved on {new Date(sd.createdAt).toLocaleDateString()}
                            </p>
                          </div>

                          <div className="flex flex-col gap-2 pt-2">
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  const newCartItem: MagnetDesign = {
                                    ...sd.design,
                                    id: 'mag_' + Math.random().toString(36).substring(2, 9),
                                    quantity: sd.design.quantity || 10
                                  };
                                  setCart(prev => [newCartItem, ...prev]);
                                  showToast(`"${sd.name}" template added to cart!`, 'success');
                                }}
                                className="flex-1 bg-[#062d4c] hover:bg-[#114e74] text-white font-semibold py-2.5 px-3 rounded-[5px] text-xs transition active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer"
                              >
                                <Plus className="w-3.5 h-3.5" /> Re-Order
                              </button>
                              <button
                                onClick={() => {
                                  setEditingDesignId(sd.id);
                                  if (sd.design.isMomentsTemplate) {
                                    setActiveTab('moments');
                                  } else {
                                    setActiveTab('shop');
                                  }
                                }}
                                className="flex-1 bg-[#e9f2f6] hover:bg-[#d8eeee] text-[#062d4c] font-semibold py-2.5 px-3 rounded-[5px] text-xs transition active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer border border-[#c5d5de]"
                              >
                                <Edit3 className="w-3.5 h-3.5" /> Customize
                              </button>
                              <button
                                onClick={() => setDesignToDelete(sd)}
                                className="bg-white hover:bg-rose-50 border border-[#d7e0e3] hover:border-rose-200 text-[#566a78] hover:text-rose-600 p-2.5 rounded-[5px] transition duration-150 cursor-pointer"
                                title="Delete saved design"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (loadingOrders && orderHistory.length === 0) ? (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start animate-pulse" id="orders-ledger-skeleton">
                  <div className="lg:col-span-6 space-y-3">
                    <span className="text-[11px] font-semibold text-[#566a78] uppercase tracking-wider block">Your Purchase History Ledger</span>
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="p-4 bg-white border border-slate-200 rounded-[5px] flex justify-between items-center shadow-sm">
                        <div className="space-y-2 w-2/3">
                          <div className="flex items-center gap-2">
                            <div className="h-4 bg-slate-200 rounded w-24" />
                            <div className="h-4 bg-slate-150 rounded w-12" />
                          </div>
                          <div className="h-3 bg-slate-150 rounded w-1/2" />
                          <div className="h-5 bg-slate-200 rounded w-28 mt-1.5" />
                        </div>
                        <div className="text-right flex flex-col items-end gap-1 w-1/4">
                          <div className="h-4 bg-slate-200 rounded w-12" />
                          <div className="h-3 bg-slate-150 rounded w-8 mt-1" />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="lg:col-span-6 bg-slate-50/50 border border-slate-200 rounded-[5px] p-6 space-y-6">
                    <div className="space-y-2">
                      <div className="h-5 bg-slate-200 rounded w-1/2" />
                      <div className="h-3 bg-slate-150 rounded w-1/3" />
                    </div>
                    <div className="space-y-4">
                      {[1, 2, 3].map((step) => (
                        <div key={step} className="flex gap-4 items-start">
                          <div className="w-8 h-8 rounded-full bg-slate-200 shrink-0" />
                          <div className="flex-grow space-y-2 pt-1">
                            <div className="h-4 bg-slate-200 rounded w-1/3" />
                            <div className="h-3 bg-slate-150 rounded w-1/2" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (orderHistory.length === 0 && !selectedOrderTracking) ? (
                <div className="bg-[#fffefb] border border-[#d7e0e3] p-12 rounded-[5px] text-center space-y-4 shadow-xs">
                  <Clock className="w-8 h-8 text-[#006cbb] mx-auto" />
                  <h3 className="text-lg font-semibold text-[#062d4c]">No active orders placed yet</h3>
                  <p className="text-xs text-[#566a78] max-w-sm mx-auto leading-relaxed">
                    Once you add custom magnets to your cart and complete checkout, your trackable package records will appear here automatically.
                  </p>
                  <button
                    onClick={() => setActiveTab('shop')}
                    className="btn btn-dark !min-h-[46px] !py-2.5 !px-6 !text-xs mx-auto"
                  >
                    <Palette className="w-4 h-4" />
                    <span>Make Your First Magnet</span>
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                  {/* Orders table - 6 cols */}
                  <div className="lg:col-span-6 space-y-3">
                    <span className="text-[11px] font-semibold text-[#566a78] uppercase tracking-wider block">Your Purchase History Ledger</span>
                    {orderHistory.filter((o) => o.status !== 'pending' && o.status !== 'cancelled').length > 0 ? (
                      orderHistory.filter((o) => o.status !== 'pending' && o.status !== 'cancelled').map((order) => {
                        const statusColors = {
                          pending: 'bg-slate-100 text-slate-600 border-slate-200',
                          paid: 'bg-[#d8eeee] text-[#062d4c] border-[#317c8b]/30',
                          completed: 'bg-[#e9f2f6] text-[#006cbb] border-[#bdd5e3]',
                          shipped: 'bg-[#9ce3d5]/60 text-[#062d4c] border-[#33c6c5]',
                          delivered: 'bg-sky-50 text-sky-700 border-sky-200',
                          refunded: 'bg-rose-50 text-rose-700 border-rose-200',
                        }[order.status] || 'bg-slate-50 border-slate-200';

                        const itemsCount = order.magnets.reduce((sum, m) => sum + m.quantity, 0);

                        return (
                          <div 
                            key={order.id} 
                            onClick={() => setSelectedOrderTracking(order)}
                            className={`p-4 bg-[#fffefb] border rounded-[5px] cursor-pointer hover:bg-[#e9f2f6]/40 transition flex justify-between items-center shadow-xs ${
                              selectedOrderTracking?.id === order.id ? 'border-[#006cbb] ring-2 ring-[#006cbb]/15' : 'border-[#d7e0e3]'
                            }`}
                          >
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-[#006cbb]">{order.id}</span>
                                <span className={`px-2 py-0.5 rounded-[3px] border text-[10px] font-semibold uppercase ${statusColors}`}>
                                  {mapStatusToLabel(order.status)}
                                </span>
                              </div>
                              
                              <p className="text-xs text-[#566a78] mt-1 font-medium">
                                Placed on {new Date(order.createdAt).toLocaleDateString()} &bull; {itemsCount} custom magnet(s)
                              </p>
                              {(order.carrier || order.trackingNumber) && (() => {
                                const effectiveOrderCarrier = (!order.carrier || order.carrier === 'USPS') ? 'Canada Post' : order.carrier;
                                const orderTrackingUrl = getCarrierTrackingUrl(effectiveOrderCarrier, order.trackingNumber);
                                return (
                                  <div className="mt-1.5">
                                    <p className="text-xs text-[#082d48] font-semibold flex items-center gap-1.5 flex-wrap">
                                      <Truck className="w-3.5 h-3.5 text-[#006cbb] shrink-0" />
                                      <span>Carrier: <strong className="text-[#062d4c] uppercase">{effectiveOrderCarrier}</strong></span>
                                      {order.trackingNumber && (
                                        <span className="text-[#006cbb] bg-[#e9f2f6] px-1.5 py-0.5 rounded-[3px] border border-[#bdd5e3]">
                                          #{order.trackingNumber}
                                        </span>
                                      )}
                                    </p>
                                    {orderTrackingUrl && (
                                      <a
                                        href={orderTrackingUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#006cbb] hover:text-[#062d4c] underline underline-offset-2 mt-1 transition-colors"
                                      >
                                        <span>Track on {effectiveOrderCarrier}</span>
                                        <ExternalLink className="w-3 h-3 shrink-0" />
                                      </a>
                                    )}
                                  </div>
                                );
                              })()}

                              {order.status !== 'pending' && order.status !== 'cancelled' && (
                                <div className="mt-2 flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      generateInvoicePDF(order);
                                    }}
                                    className="text-xs text-[#062d4c] hover:text-[#006cbb] font-semibold flex items-center gap-1 hover:underline cursor-pointer bg-[#e9f2f6] hover:bg-[#d8eeee] px-2.5 py-1 rounded-[4px] border border-[#c5d5de] transition"
                                    title="Download paid invoice PDF"
                                  >
                                    <FileText className="w-3.5 h-3.5 text-[#006cbb]" /> Download Invoice
                                  </button>
                                </div>
                              )}
                            </div>

                            <div className="text-right flex flex-col items-end gap-1 text-sm font-bold text-[#062d4c]">
                              <span>${(order.total ?? 0).toFixed(2)}</span>
                              <div className="flex items-center gap-1 text-xs text-[#566a78] font-medium">
                                <span>Details</span>
                                <ChevronRight className="w-3.5 h-3.5 text-[#566a78]" />
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      selectedOrderTracking && selectedOrderTracking.status !== 'pending' && selectedOrderTracking.status !== 'cancelled' && (
                        <div 
                          className="p-4 bg-[#fffefb] border border-[#006cbb] ring-2 ring-[#006cbb]/15 rounded-[5px] flex justify-between items-center shadow-xs"
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-[#006cbb]">{selectedOrderTracking.id}</span>
                              <span className="px-2 py-0.5 rounded-[3px] border text-[10px] font-semibold uppercase bg-[#d8eeee] text-[#062d4c] border-[#317c8b]/30">
                                {mapStatusToLabel(selectedOrderTracking.status)}
                              </span>
                            </div>
                            
                            <p className="text-xs text-[#566a78] mt-1 font-medium">
                              Placed on {new Date(selectedOrderTracking.createdAt).toLocaleDateString()} &bull; {selectedOrderTracking.magnets.reduce((sum, m) => sum + m.quantity, 0)} custom magnet(s)
                            </p>

                            <div className="mt-2 flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  generateInvoicePDF(selectedOrderTracking);
                                }}
                                className="text-xs text-[#062d4c] hover:text-[#006cbb] font-semibold flex items-center gap-1 hover:underline cursor-pointer bg-[#e9f2f6] hover:bg-[#d8eeee] px-2.5 py-1 rounded-[4px] border border-[#c5d5de] transition"
                                title="Download paid invoice PDF"
                              >
                                <FileText className="w-3.5 h-3.5 text-[#006cbb]" /> Download Invoice
                              </button>
                            </div>
                          </div>

                          <div className="text-right text-sm font-bold text-[#062d4c]">
                            <span>${(selectedOrderTracking.total ?? 0).toFixed(2)}</span>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Tracking Progress Trajectory details - 6 cols */}
                  <div className="lg:col-span-6 space-y-3">
                    <span className="text-[11px] font-semibold text-[#566a78] uppercase tracking-wider block">Live Courier Shipping Tracker</span>
                    {selectedOrderTracking ? (
                      renderTrackingCard(selectedOrderTracking)
                    ) : (
                      <div className="bg-[#fffefb] border border-[#d7e0e3] rounded-[5px] p-10 text-center text-[#566a78] shadow-xs">
                        <Truck className="w-8 h-8 text-[#006cbb] mx-auto mb-2" />
                        <p className="text-xs font-medium">Select any order from your ledger list on the left to view real-time shipping checkpoints and tracking links.</p>
                      </div>
                    )}
                  </div>
                </div>
              )
            ) : (
              // Guest Flow Tracker
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                <div className="lg:col-span-5 bg-[#fffefb] border border-[#d7e0e3] p-6 sm:p-7 rounded-[5px] shadow-xs space-y-5">
                  <div className="p-4 bg-[#e9f2f6] border border-[#c5d5de] rounded-[5px]">
                    <h3 className="text-xs font-semibold text-[#062d4c] flex items-center gap-2 uppercase tracking-wider">
                      <Search className="w-4 h-4 text-[#006cbb]" />
                      <span>Quick Guest Order Lookup</span>
                    </h3>
                    <p className="text-[#566a78] text-xs mt-1.5 leading-relaxed">
                      Enter your Order Number (e.g., ORD-7824) and the email address used at checkout. No account sign-in required.
                    </p>
                  </div>

                  <form onSubmit={handleGuestLookup} className="space-y-4">
                    {lookupError && (
                      <div className="p-3 bg-rose-50 border border-rose-200 rounded-[5px] text-rose-700 text-xs flex items-start gap-2 font-medium">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" id="lookup-fail-alert" />
                        <span>{lookupError}</span>
                      </div>
                    )}

                    <div>
                      <label htmlFor="tracker_lookup_order" className="text-xs font-semibold text-[#062d4c] block mb-1.5">Order Number</label>
                      <input
                        id="tracker_lookup_order"
                        type="text"
                        required
                        value={lookupOrderId}
                        onChange={(e) => setLookupOrderId(e.target.value)}
                        placeholder="e.g. ORD-8431"
                        className="w-full px-3.5 py-2.5 text-xs bg-white border border-[#becbd0] rounded-[4px] text-[#082d48] focus:outline-none focus:border-[#006cbb] placeholder:text-[#566a78]/60 font-semibold uppercase tracking-wider"
                      />
                    </div>

                    <div>
                      <label htmlFor="tracker_lookup_email" className="text-xs font-semibold text-[#062d4c] block mb-1.5">Email Address</label>
                      <input
                        id="tracker_lookup_email"
                        type="email"
                        required
                        value={lookupEmail}
                        onChange={(e) => setLookupEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full px-3.5 py-2.5 text-xs bg-white border border-[#becbd0] rounded-[4px] text-[#082d48] focus:outline-none focus:border-[#006cbb] placeholder:text-[#566a78]/60 font-medium"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={lookupLoading}
                      className="btn btn-dark w-full !min-h-[48px] !py-3 !text-xs disabled:opacity-50"
                    >
                      {lookupLoading && (
                        <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      )}
                      <span>{lookupLoading ? 'Validating purchase records...' : 'Track Magnet Delivery'}</span>
                      <svg className="icon"><use href="#arrow" /></svg>
                    </button>
                  </form>
                </div>

                <div className="lg:col-span-7 space-y-3">
                  <span className="text-[11px] font-semibold text-[#566a78] uppercase tracking-wider block">Live Courier Shipping Tracker</span>
                  {guestLookedUpOrder ? (
                    <div>
                      <div className="mb-3 flex justify-between items-center">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedOrderTracking(null);
                            setGuestLookedUpOrder(null);
                          }}
                          className="text-xs text-[#006cbb] hover:text-[#062d4c] font-semibold flex items-center gap-1 hover:underline cursor-pointer"
                        >
                          ← Search another order
                        </button>
                      </div>
                      {renderTrackingCard(guestLookedUpOrder)}
                    </div>
                  ) : (
                    <div className="bg-[#fffefb] border border-[#d7e0e3] rounded-[5px] p-12 text-center text-[#566a78] shadow-xs space-y-2">
                      <Truck className="w-8 h-8 text-[#006cbb] mx-auto mb-2" />
                      <p className="text-sm font-semibold text-[#062d4c]">Awaiting Lookup Details</p>
                      <p className="text-xs text-[#566a78] max-w-xs mx-auto leading-relaxed">Enter your order reference number and checkout email on the left to view real-time courier tracking details.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'admin' && auth.token && auth.user?.role === 'admin' && (
          <div className="space-y-6 animate-fadeIn">
            <Suspense fallback={<div className="py-12 text-center text-slate-400 font-medium">Loading Admin Console...</div>}>
              <AdminDashboard 
                token={auth.token} 
                onPricingUpdated={fetchPricing} 
                onSessionExpired={() => handleSessionExpired(true)}
                savedDesigns={savedDesigns}
              />
            </Suspense>
          </div>
        )}

        {activeTab === 'privacy' && (
          <Suspense fallback={<div className="py-12 text-center text-slate-400 font-medium">Loading...</div>}>
            <PrivacyPolicy onBackToHome={() => { setActiveTab('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
          </Suspense>
        )}

        {activeTab === 'terms' && (
          <Suspense fallback={<div className="py-12 text-center text-slate-400 font-medium">Loading...</div>}>
            <TermsOfService onBackToHome={() => { setActiveTab('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
          </Suspense>
        )}

        {activeTab === 'news' && (
          <Suspense fallback={<div className="py-12 text-center text-slate-400 font-medium">Loading News...</div>}>
            <NewsPage 
              selectedSlug={selectedNewsSlug} 
              onSelectSlug={setSelectedNewsSlug} 
              onNavigateToTab={(tab) => { setActiveTab(tab); setSelectedNewsSlug(null); }} 
            />
          </Suspense>
        )}

        {activeTab === 'fundraising' && (
          <Suspense fallback={<div className="py-12 text-center text-slate-400 font-medium">Loading Fundraising Campaign Hub...</div>}>
            <FundraisingPage 
              onNavigateToShop={() => { setActiveTab('shop'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              onNavigateToHome={() => { setActiveTab('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            />
          </Suspense>
        )}

        {activeTab === 'giftcards' && (
          <Suspense fallback={<div className="py-12 text-center text-slate-400 font-medium">Loading Digital Gift Cards...</div>}>
            <GiftCardsPage
              pricing={pricing}
              defaultSenderName={auth.user?.name || ''}
              defaultSenderEmail={auth.user?.email || ''}
              onApplyGiftCodeAndShop={(code) => {
                setCouponCodeInput(code.toUpperCase());
                handleApplyCoupon(code.toUpperCase());
                setActiveTab('shop');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              onNavigateToStudio={() => { setActiveTab('shop'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            />
          </Suspense>
        )}

        {isUseCaseSlug(activeTab) && (
          <Suspense fallback={<div className="py-12 text-center text-slate-400 font-medium">Loading Custom Magnet Guide...</div>}>
            <UseCaseLandingPage
              slug={activeTab}
              pricing={pricing}
              onNavigateToStudio={() => { setActiveTab('shop'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              onNavigateToMoments={() => { setActiveTab('moments'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              onNavigateToGiftCards={() => { setActiveTab('giftcards'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              onNavigateToFundraising={() => { setActiveTab('fundraising'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              onSelectUseCase={(nextSlug) => { setActiveTab(nextSlug); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            />
          </Suspense>
        )}

      </main>

      {/* FOOTER */}
      <footer className="site-footer sm-fresh-design text-left mt-auto">
        <div className="wrap">
          <div className="footer-grid">
            <div className="footer-brand">
              <a
                href="./"
                aria-label="SquareMagnets home"
                id="footer-brand-logo"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('home');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                <img
                  src="/squaremagnets-logo.webp"
                  alt="SquareMagnets.ca — Custom Magnets. BIG Impact."
                  width="1293"
                  height="299"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              </a>
              <p>
                Little squares for the moments that matter.<br />
                Custom photo and logo magnets, handcrafted in Kamloops, British Columbia.
              </p>
              <div className="socials">
                <a href="https://www.facebook.com/profile.php?id=61591263997706" target="_blank" rel="noopener noreferrer">Facebook ↗</a>
                <a href="https://www.instagram.com/square.magnets/" target="_blank" rel="noopener noreferrer">Instagram ↗</a>
                <a href="https://ca.pinterest.com/squaremagnetsco/" target="_blank" rel="noopener noreferrer">Pinterest ↗</a>
              </div>
            </div>

            <div className="footer-col">
              <h3>Create something yours</h3>
              <a
                href="/studio"
                onMouseEnter={preloadMagnetDesigner}
                onTouchStart={preloadMagnetDesigner}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('shop');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Upload &amp; Customize
              </a>
              <a
                href="/browse"
                onMouseEnter={preloadPremade}
                onTouchStart={preloadPremade}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('browse');
                  window.scrollTo({ top: 0, behavior: 'instant' });
                }}
              >
                Browse Designs
              </a>
              <a
                href="/moments"
                onMouseEnter={preloadMomentsCreator}
                onTouchStart={preloadMomentsCreator}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('moments');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Moments Creator
              </a>
              <a
                href="/fundraising"
                onMouseEnter={preloadFundraising}
                onTouchStart={preloadFundraising}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('fundraising');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                School &amp; Group Fundraising
              </a>
              <a
                href="/gift-cards"
                onMouseEnter={preloadGiftCards}
                onTouchStart={preloadGiftCards}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('giftcards');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Digital Gift Cards
              </a>
            </div>

            <div className="footer-col">
              <h3>A little help</h3>
              <a
                href="/track"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('dashboard');
                  setDashboardTab('orders');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Track Order
              </a>
              <a
                href="/cart"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('cart');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Shopping Cart
              </a>
              <a
                href="/?page=saved_designs"
                onClick={(e) => {
                  e.preventDefault();
                  if (auth.token) {
                    setActiveTab('dashboard');
                    setDashboardTab('saved_designs');
                    setSelectedOrderTracking(null);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } else {
                    setShowAuthModal(true);
                  }
                }}
              >
                Sign in / My Account
              </a>
              <a
                href="#questions"
                onClick={(e) => {
                  e.preventDefault();
                  if (activeTab !== 'home') {
                    setActiveTab('home');
                    setTimeout(() => {
                      document.getElementById('questions')?.scrollIntoView({ behavior: 'smooth' });
                    }, 50);
                  } else {
                    document.getElementById('questions')?.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                Frequently Asked Questions
              </a>
              <a
                href="/news"
                onMouseEnter={preloadNews}
                onTouchStart={preloadNews}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('news');
                  setSelectedNewsSlug(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Blog &amp; Design Guides
              </a>
            </div>

            <div className="footer-col">
              <h3>Shop by occasion</h3>
              <a
                href="/wedding-favors"
                onMouseEnter={preloadUseCasePage}
                onTouchStart={preloadUseCasePage}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('wedding-favors');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Wedding Favours &amp; Save the Dates
              </a>
              <a
                href="/pet-magnets"
                onMouseEnter={preloadUseCasePage}
                onTouchStart={preloadUseCasePage}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('pet-magnets');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Custom Dog &amp; Cat Magnets
              </a>
              <a
                href="/baby-milestones"
                onMouseEnter={preloadUseCasePage}
                onTouchStart={preloadUseCasePage}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('baby-milestones');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Baby First Year &amp; Ultrasounds
              </a>
              <a
                href="/business-magnets"
                onMouseEnter={preloadUseCasePage}
                onTouchStart={preloadUseCasePage}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('business-magnets');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Business &amp; QR Promo Magnets
              </a>
              <a
                href="/memorial-magnets"
                onMouseEnter={preloadUseCasePage}
                onTouchStart={preloadUseCasePage}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('memorial-magnets');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Celebration of Life Keepsakes
              </a>
            </div>
          </div>

          <div className="footer-bottom">
            <span>© {new Date().getFullYear()} SquareMagnets.ca. Handcrafted in Canada.</span>
            <div className="footer-legal">
              <a
                href="/privacy"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('privacy');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('terms');
                  setSelectedOrderTracking(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Terms of Service
              </a>
              <a
                href="/?page=admin"
                onClick={(e) => {
                  e.preventDefault();
                  if (auth.token && auth.user?.role === 'admin') {
                    setActiveTab('admin');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } else {
                    setShowAuthModal(true);
                  }
                }}
              >
                Admin
              </a>
            </div>
            <span>All prices in CAD.</span>
          </div>
        </div>
      </footer>

      {/* Authentication Overlay Modal */}
      {showAuthModal && (
        <Suspense fallback={null}>
          <AuthModal 
            onSuccess={handleAuthSuccess} 
            onClose={() => {
              setShowAuthModal(false);
              setDesignToSave(null);
              setAuthModalMessage(undefined);
            }} 
            message={designToSave ? "Please log in or sign up below to save your beautiful custom magnet design so you can easily re-order it later!" : authModalMessage}
          />
        </Suspense>
      )}

      {/* Save Design Naming Modal */}
      {showSaveDesignModal && (
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-xs flex items-start sm:items-center justify-center p-4 py-8 sm:py-4 overflow-y-auto z-[60] animate-fadeIn">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl animate-scaleIn p-6 text-slate-800 my-auto">
            <div className="text-center space-y-3">
              <div className="mx-auto w-12 h-12 bg-rose-50 rounded-2xl flex items-center justify-center text-rose-500 border border-rose-100 shadow-inner">
                <Heart className="w-5 h-5 fill-rose-500" />
              </div>
              <h3 className="text-base font-extrabold text-slate-900 tracking-tight">
                {editingSavedDesignId ? 'Update Saved Design' : 'Name Your Magnet Design'}
              </h3>
              <p className="text-slate-500 text-xs font-semibold leading-relaxed">
                {editingSavedDesignId 
                  ? 'Update this saved design template with your current changes, or check the box below to save it as a brand new copy instead!'
                  : 'Give this design template a descriptive name (e.g., "Dad\'s Retirement Collage" or "Summer Trip 2026") so you can easily find it later!'}
              </p>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label htmlFor="save-design-name-input" className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Design Name</label>
                <input
                  id="save-design-name-input"
                  type="text"
                  required
                  value={saveDesignName}
                  onChange={(e) => setSaveDesignName(e.target.value)}
                  placeholder="e.g. Grandma's Birthday Gift"
                  className="w-full px-3 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:border-orange-500 font-semibold"
                />
              </div>

              {editingSavedDesignId && (
                <div className="space-y-2.5 my-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Save Action</label>
                  
                  <div
                    onClick={() => {
                      setSaveAsNewCopy(false);
                      const existing = savedDesigns.find(sd => sd.id === editingSavedDesignId);
                      if (existing) setSaveDesignName(existing.name);
                    }}
                    className={`p-3 rounded-2xl border cursor-pointer transition-all flex items-start gap-2.5 ${
                      !saveAsNewCopy 
                        ? 'bg-orange-50/80 border-orange-300 ring-2 ring-orange-400/20 text-slate-900' 
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <input
                      type="radio"
                      name="save-design-mode-option"
                      checked={!saveAsNewCopy}
                      onChange={() => {}}
                      className="mt-0.5 text-orange-500 focus:ring-orange-500 cursor-pointer"
                    />
                    <div className="text-xs">
                      <span className="font-extrabold block text-slate-900">Update Existing Design</span>
                      <span className="text-[11px] text-slate-500 font-medium leading-normal block">
                        Overwrites your saved design template with your new alterations.
                      </span>
                    </div>
                  </div>

                  <div
                    onClick={() => {
                      setSaveAsNewCopy(true);
                      const existing = savedDesigns.find(sd => sd.id === editingSavedDesignId);
                      if (existing && !saveDesignName.includes('(Copy)')) {
                        setSaveDesignName(`${existing.name} (Version 2)`);
                      }
                    }}
                    className={`p-3 rounded-2xl border cursor-pointer transition-all flex items-start gap-2.5 ${
                      saveAsNewCopy 
                        ? 'bg-orange-50/80 border-orange-300 ring-2 ring-orange-400/20 text-slate-900' 
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <input
                      type="radio"
                      name="save-design-mode-option"
                      checked={saveAsNewCopy}
                      onChange={() => {}}
                      className="mt-0.5 text-orange-500 focus:ring-orange-500 cursor-pointer"
                    />
                    <div className="text-xs">
                      <span className="font-extrabold block text-slate-900">Save as a New Design</span>
                      <span className="text-[11px] text-slate-500 font-medium leading-normal block">
                        Creates a brand new saved design file so you keep both versions!
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowSaveDesignModal(false);
                    setDesignToSave(null);
                    setEditingSavedDesignId(null);
                    setSaveAsNewCopy(false);
                    setSaveDesignName('');
                  }}
                  className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={savingDesignState || !saveDesignName.trim()}
                  onClick={handleSaveDesignConfirm}
                  className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-extrabold rounded-xl text-xs transition flex items-center justify-center gap-1 cursor-pointer"
                >
                  {savingDesignState ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>{editingSavedDesignId ? (saveAsNewCopy ? 'Save Copy' : 'Update Design') : 'Save Design'}</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Rename Saved Design Modal */}
      {designToRename && (
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-xs flex items-start sm:items-center justify-center p-4 py-8 sm:py-4 overflow-y-auto z-[60] animate-fadeIn">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl animate-scaleIn p-6 text-slate-800 my-auto">
            <div className="text-center space-y-3">
              <div className="mx-auto w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-500 border border-orange-100 shadow-inner">
                <Edit3 className="w-5 h-5" />
              </div>
              <h3 className="text-base font-extrabold text-slate-900 tracking-tight">Rename Your Design</h3>
              <p className="text-slate-500 text-xs font-semibold leading-relaxed">
                Choose a new descriptive name for this saved design template.
              </p>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label htmlFor="rename-saved-design-input" className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">New Design Name</label>
                <input
                  id="rename-saved-design-input"
                  type="text"
                  required
                  value={renameInputName}
                  onChange={(e) => setRenameInputName(e.target.value)}
                  placeholder="e.g. Summer Vacation Collage"
                  className="w-full px-3 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:border-orange-500 font-semibold"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setDesignToRename(null);
                    setRenameInputName('');
                  }}
                  className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={renamingInProgress || !renameInputName.trim() || renameInputName.trim() === designToRename.name}
                  onClick={handleRenameSavedDesignConfirm}
                  className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-extrabold rounded-xl text-xs transition flex items-center justify-center gap-1 cursor-pointer"
                >
                  {renamingInProgress ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>Save Name</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Delete Saved Design Confirmation Modal */}
      {designToDelete && (
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-xs flex items-start sm:items-center justify-center p-4 py-8 sm:py-4 overflow-y-auto z-[60] animate-fadeIn">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl animate-scaleIn p-6 text-slate-800 my-auto">
            <div className="text-center space-y-3">
              <div className="mx-auto w-12 h-12 bg-rose-50 rounded-2xl flex items-center justify-center text-rose-500 border border-rose-100 shadow-inner">
                <Trash2 className="w-5 h-5" />
              </div>
              <h3 className="text-base font-extrabold text-slate-900 tracking-tight">Delete Saved Design?</h3>
              <p className="text-slate-500 text-xs font-semibold leading-relaxed">
                Are you sure you want to delete <strong className="text-slate-900">"{designToDelete.name}"</strong>? This action cannot be undone.
              </p>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setDesignToDelete(null)}
                className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition cursor-pointer"
              >
                No, Keep It
              </button>
              <button
                type="button"
                disabled={deletingInProgress}
                onClick={handleDeleteSavedDesignConfirm}
                className="flex-1 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-extrabold rounded-xl text-xs transition flex items-center justify-center gap-1 cursor-pointer shadow-sm shadow-rose-100"
              >
                {deletingInProgress ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Delete Design</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && auth.token && (
        <Suspense fallback={null}>
          <ChangePasswordModal 
            token={auth.token} 
            onClose={() => setShowChangePasswordModal(false)} 
          />
        </Suspense>
      )}

      {/* Profile Settings Modal */}
      {showProfileModal && auth.token && auth.user && (
        <Suspense fallback={null}>
          <ProfileModal 
            token={auth.token}
            initialName={auth.user.name}
            initialEmail={auth.user.email}
            onSuccess={handleProfileSuccess}
            onClose={() => setShowProfileModal(false)}
          />
        </Suspense>
      )}

      {/* Interactive Print-Proof Zoom Preview Modal */}
      {previewItem && (
        <div 
          onClick={() => setPreviewItem(null)}
          className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-start sm:items-center justify-center p-4 py-8 sm:py-4 overflow-y-auto z-50 animate-fadeIn select-none"
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl border border-slate-100 shadow-2xl w-full max-w-sm p-4 sm:p-6 relative flex flex-col items-center text-slate-800 my-auto"
          >
            <div className="flex justify-between items-center w-full mb-4">
              <div>
                <h3 className="text-xs font-black text-slate-900 flex items-center gap-1.5 uppercase tracking-wider">
                  <Search className="w-3.5 h-3.5 text-[#008080]" />
                  <span>Print Proof Preview</span>
                </h3>
                <p className="text-[10px] text-slate-400 font-mono mt-0.5">Physical finished front vs print sheet</p>
              </div>
              <button
                type="button"
                onClick={() => setPreviewItem(null)}
                className="w-7 h-7 flex items-center justify-center bg-slate-100 hover:bg-slate-200 rounded-full text-slate-500 hover:text-slate-900 font-extrabold text-sm leading-none cursor-pointer transition-colors"
              >
                &times;
              </button>
            </div>

            {/* Proof Mode Toggle Switch Selector */}
            <div className="w-full grid grid-cols-2 bg-slate-100 p-1 rounded-xl mb-4 text-xs font-bold text-center">
              <button
                type="button"
                onClick={() => setProofMode('finished')}
                className={`py-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                  proofMode === 'finished' 
                    ? 'bg-white text-slate-900 shadow-xs' 
                    : 'text-slate-450 hover:text-slate-700'
                }`}
              >
                <Magnet className="w-3.5 h-3.5" />
                <span>Finished Front (2.5")</span>
              </button>
              <button
                type="button"
                onClick={() => setProofMode('bleed')}
                className={`py-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                  proofMode === 'bleed' 
                    ? 'bg-white text-indigo-650 shadow-xs' 
                    : 'text-slate-450 hover:text-slate-700'
                }`}
              >
                <Scissors className="w-3.5 h-3.5" />
                <span>Full Print Sheet</span>
              </button>
            </div>

            {/* The main picture block: 100% distortion-free contain, styled like a physical magnet */}
            <div className="relative w-full aspect-square bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden shadow-md p-4 flex items-center justify-center">
              {/* Slate graph grid lines backdrop */}
              <div className="absolute inset-0 bg-[radial-gradient(#e2e8f0_1.5px,transparent_1.5px)] [background-size:16px_16px] opacity-60" />
              
              {/* Authentic 3D rounded card matching the product */}
              <div className="relative w-full h-full rounded bg-white shadow-xl overflow-hidden border border-slate-200">
                {/* 3D Glossy laminate reflection element overlays */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/15 to-white/20 pointer-events-none z-10" />
                <div className="absolute inset-x-0 top-0 h-[20%] bg-white/10 pointer-events-none z-10 transform -skew-y-12 translate-y-[-10px]" />
                
                <img 
                  src={previewItem.croppedImageUrl} 
                  className="w-full h-full object-contain bg-white transition-transform duration-300 ease-out" 
                  style={{
                    transform: proofMode === 'finished' ? 'scale(1.171)' : 'scale(1)',
                  }}
                  alt="High definition print-proof proofing block"
                  draggable={false}
                  decoding="async"
                />

                {/* If bleed mode is on, draw guidelines and shade outer bleed area */}
                {proofMode === 'bleed' && (
                  <>
                    {/* Semi-transparent dimmed frame for the 7.3% wrap fold boundaries */}
                    <div className="absolute inset-0 border-[7.3%] border-slate-950/50 pointer-events-none z-20 transition-all" />
                    
                    {/* Finished face fold guideline (7.3% inset) */}
                    <div 
                      className="absolute border-2 border-dashed border-indigo-500 rounded pointer-events-none z-30"
                      style={{ top: '7.3%', bottom: '7.3%', left: '7.3%', right: '7.3%' }}
                    >
                      <span className="absolute top-1 right-1 bg-indigo-600/95 text-white text-[6.5px] font-black uppercase px-1 py-0.5 rounded shadow tracking-wider leading-none z-30 select-none">
                        FOLD LINE (2.50" FACE)
                      </span>
                    </div>

                    {/* Cutter sheet size outline */}
                    <div 
                      className="absolute border border-dashed border-rose-500 rounded pointer-events-none z-30"
                      style={{ top: '0.5%', bottom: '0.5%', left: '0.5%', right: '0.5%' }}
                    >
                      <span className="absolute bottom-1 left-1 bg-rose-600/95 text-white text-[6.5px] font-black uppercase px-1 py-0.5 rounded shadow tracking-wider leading-none z-30 select-none">
                        SHEET CUT EDGE (2.929")
                      </span>
                    </div>
                  </>
                )}

                {/* Finished front mode border helper */}
                {proofMode === 'finished' && (
                  <div className="absolute inset-0 border-2 border-emerald-500/20 pointer-events-none z-20" />
                )}
              </div>
            </div>

            {/* Helper Alert Text */}
            <div className="w-full mt-2 text-[10px] leading-tight text-center font-semibold text-slate-500">
              {proofMode === 'finished' ? (
                <p className="text-emerald-650 bg-emerald-50/70 border border-emerald-100 rounded-lg px-2.5 py-1.5 flex items-center justify-center gap-1">
                  <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span><b>Showing Finished Face (2.5" x 2.5")</b>. This is exactly what will be visible on the front of your custom magnet.</span>
                </p>
              ) : (
                <p className="text-indigo-650 bg-indigo-50/70 border border-indigo-100 rounded-lg px-2.5 py-1.5 flex items-center justify-center gap-1">
                  <Info className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                  <span><b>Showing Full Print Sheet with Bleeds</b>. The outer dimmed area folds/wraps around the steel plate of the magnet.</span>
                </p>
              )}
            </div>

            {/* Specifications Details layout */}
            <div className="w-full mt-3 p-3 bg-slate-50 border border-slate-200 rounded-2xl text-left space-y-1.5">
              <span className="text-[9px] font-black tracking-wider text-slate-400 uppercase block">Magnet Order Specifications</span>
              
              <div className="grid grid-cols-2 gap-2 text-[10px] font-semibold">
                <div>
                  <span className="text-slate-450 block text-[9px] font-bold">TUNED COLOR FILTER</span>
                  <span className="text-orange-605 font-black uppercase font-mono">{previewItem.filter}</span>
                </div>
                <div>
                  <span className="text-slate-455 block text-[9px] font-bold">QTY COMMITTED</span>
                  <span className="text-slate-800 font-black font-mono">{previewItem.quantity} unit(s)</span>
                </div>
              </div>

              {previewItem.text?.text ? (
                <div className="pt-1.5 border-t border-slate-200 mt-1">
                  <span className="text-slate-450 block text-[9px] font-bold">TYPOGRAPHY LABEL OVERLAY</span>
                  <p className="text-[11px] text-slate-800 italic font-bold leading-relaxed bg-white px-2 py-1.5 rounded border border-slate-150 mt-1 max-h-16 overflow-y-auto font-sans break-words">
                    "{previewItem.text.text}"
                  </p>
                  <div className="flex justify-between text-[8px] text-slate-450 font-bold font-mono mt-1 px-1">
                    <span>FONT: {previewItem.text.fontFamily || 'Inter'}</span>
                    <span>SIZE: {previewItem.text.fontSize || 20}px</span>
                  </div>
                </div>
              ) : (
                <div className="pt-2 border-t border-slate-200 mt-1.5">
                  <span className="text-slate-450 block text-[9px] font-bold">TYPOGRAPHY LABEL OVERLAY</span>
                  <p className="text-[10px] text-slate-400 italic mt-0.5 font-bold">No custom text labels requested</p>
                </div>
              )}
            </div>

            {(() => {
              const matchedSaved = getSavedDesignForMagnet(previewItem);
              return (
                <button
                  type="button"
                  onClick={() => {
                    handleSaveDesign(previewItem, matchedSaved?.id);
                  }}
                  className={`w-full mt-3.5 py-2.5 text-xs font-black rounded-xl shadow-sm transition-all cursor-pointer flex items-center justify-center gap-2 border ${
                    matchedSaved 
                      ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200' 
                      : 'bg-rose-550 hover:bg-rose-600 text-white border-transparent'
                  }`}
                >
                  {matchedSaved ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>Saved: Click to update "{matchedSaved.name}"</span>
                    </>
                  ) : (
                    <>
                      <Heart className="w-3.5 h-3.5" />
                      <span>Save Design to Favorites</span>
                    </>
                  )}
                </button>
              );
            })()}

            <button
              type="button"
              onClick={() => setPreviewItem(null)}
              className="w-full mt-2 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-black rounded-xl shadow-md active:scale-[0.98] transition-all cursor-pointer"
            >
              Resume Checkout
            </button>
          </div>
        </div>
      )}

      {/* STICKY "DESIGN MAGNETS" MOBILE BAR - Appears when browsing past the hero on mobile */}
      {activeTab === 'home' && (
        <div
          id="sticky-mobile-design-bar"
          className={`mobile-cta sm-fresh-design ${showStickyMobileBar ? 'visible' : ''}`}
        >
          <p>
            YOUR PHOTOS. YOUR LITTLE SQUARES.
            <strong>From ${pricing.pricePerMagnet.toFixed(2)} CAD</strong>
          </p>
          <a
            id="sticky-mobile-upload-btn"
            className="btn btn-dark"
            href="/studio"
            onClick={(e) => {
              e.preventDefault();
              setActiveTab('shop');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            Start creating
            <svg className="icon"><use href="#arrow" /></svg>
          </a>
        </div>
      )}

      {/* Floating Global Success/Info/Error Toast Notification Banner */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-slate-900 text-white px-4 py-3 rounded-2xl shadow-2xl flex items-center gap-3 z-[100] animate-slideUp border border-slate-800 max-w-sm">
          <div className={`p-1.5 rounded-xl flex items-center justify-center ${
            toast.type === 'success' 
              ? 'bg-emerald-500/20 text-emerald-400' 
              : toast.type === 'error' 
                ? 'bg-rose-500/20 text-rose-400' 
                : 'bg-blue-500/20 text-blue-400'
          }`}>
            {toast.type === 'success' ? (
              <Check className="w-4 h-4 text-emerald-400" />
            ) : toast.type === 'error' ? (
              <AlertCircle className="w-4 h-4 text-rose-400" />
            ) : (
              <Info className="w-4 h-4 text-blue-400" />
            )}
          </div>
          <p className="text-xs font-bold tracking-tight pr-1 leading-relaxed">{toast.message}</p>
          <button 
            type="button"
            onClick={() => setToast(null)}
            className="text-slate-400 hover:text-white transition font-black text-sm p-1 cursor-pointer leading-none"
          >
            &times;
          </button>
        </div>
      )}

    </div>
  );
}
